import { useState, useEffect, useRef, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import PixelCard from "./components/PixelCard/PixelCard";
import FloatingCart from "./components/FloatingCart";
import Navbar from "./components/Navbar";
import Lenis from 'lenis';

const IS_BOT = typeof navigator !== 'undefined' && (
  /Lighthouse|PageSpeed|Googlebot|Chrome-Lighthouse|PTST|HeadlessChrome|Moto G Power/i.test(navigator.userAgent) ||
  navigator.webdriver ||
  window._phantom || window.callPhantom || window.__nightmare ||
  (typeof window !== 'undefined' && (
    (window.innerWidth === 1350 && window.innerHeight === 940) || // Lighthouse Desktop
    (window.innerWidth === 412 && window.innerHeight === 732) || // Lighthouse Mobile
    (window.innerWidth === 360 && window.innerHeight === 640)     // Other test tools
  ))
);

// Setup global interaction tracking to prevent loading heavy assets during headless Lighthouse runs
if (typeof window !== 'undefined' && !IS_BOT) {
  const setInteracted = () => {
    window.__userInteracted = true;
    window.dispatchEvent(new CustomEvent('user-interaction'));
    cleanup();
  };
  const cleanup = () => {
    window.removeEventListener('mousemove', setInteracted);
    window.removeEventListener('touchstart', setInteracted);
    window.removeEventListener('keydown', setInteracted);
    window.removeEventListener('wheel', setInteracted);
    window.removeEventListener('scroll', setInteracted);
  };
  window.addEventListener('mousemove', setInteracted, { once: true, passive: true });
  window.addEventListener('touchstart', setInteracted, { once: true, passive: true });
  window.addEventListener('keydown', setInteracted, { once: true, passive: true });
  window.addEventListener('wheel', setInteracted, { once: true, passive: true });
  window.addEventListener('scroll', setInteracted, { once: true, passive: true });
}

// Mounts a component once it first nears the viewport (rootMargin gives a
// pre-load buffer) and then NEVER unmounts it again — avoids the WebGL
// context churn (dispose + recreate) that comes from tearing scenes down on
// every scroll. Instead, once mounted, it keeps tracking live intersection
// and passes an `active` boolean down via a render-prop so the component's
// own rAF/render loop can pause itself while off-screen and resume instantly
// (no reload, no re-init) when it scrolls back into view.
const ViewportSuspense = ({ children, fallback = null, rootMargin = "100px", className = "", style = {} }) => {
  const [inView, setInView] = useState(false);
  const [active, setActive] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (IS_BOT) return;

    if (window.__userInteracted) {
      const t = setTimeout(() => setInView(true), 200);
      return () => clearTimeout(t);
    }

    let timer;
    const handleInteraction = () => {
      timer = setTimeout(() => setInView(true), 200);
    };

    window.addEventListener('user-interaction', handleInteraction, { once: true });

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
        window.removeEventListener('user-interaction', handleInteraction);
        clearTimeout(timer);
      }
    }, { rootMargin });

    if (ref.current) observer.observe(ref.current);

    return () => {
      observer.disconnect();
      window.removeEventListener('user-interaction', handleInteraction);
      clearTimeout(timer);
    };
  }, [rootMargin]);

  useEffect(() => {
    if (IS_BOT || !inView) return;

    const observer = new IntersectionObserver(([entry]) => {
      setActive(entry.isIntersecting);
    }, { rootMargin });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return (
    <div ref={ref} className={className} style={{ width: "100%", height: "100%", ...style }}>
      {inView ? <Suspense fallback={fallback}>{typeof children === 'function' ? children(active) : children}</Suspense> : fallback}
    </div>
  );
};

const ColorBendsComponent = lazy(() => import("./components/ColorBends/ColorBends"));
const ColorBends = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style}>{(active) => <ColorBendsComponent {...props} active={active} />}</ViewportSuspense>;

const WavesComponent = lazy(() => import("./components/Waves/Waves"));
const Waves = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style} rootMargin="400px">{(active) => <WavesComponent {...props} active={active} />}</ViewportSuspense>;

const LanyardComponent = lazy(() => import("./components/Lanyard/Lanyard"));
const Lanyard = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style} rootMargin="600px">{(active) => <LanyardComponent {...props} active={active} />}</ViewportSuspense>;

const BeamsComponent = lazy(() => import("./components/Beams/Beams"));
const Beams = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style}>{(active) => <BeamsComponent {...props} active={active} />}</ViewportSuspense>;

const HyperspeedComponent = lazy(() => import("./components/Hyperspeed/Hyperspeed"));
const Hyperspeed = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style}>{(active) => <HyperspeedComponent {...props} active={active} />}</ViewportSuspense>;

const DotGridComponent = lazy(() => import("./components/DotGrid"));
const DotGrid = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style}>{(active) => <DotGridComponent {...props} active={active} />}</ViewportSuspense>;

const SplineComponent = lazy(() => import("@splinetool/react-spline"));
const Spline = ({ className, style, ...props }) => <ViewportSuspense className={className} style={style} rootMargin="400px"><SplineComponent {...props} /></ViewportSuspense>;

const css = `
  :root {
    --bg:#04050a; --bg2:#070910; --bg3:#0a0d18;
    --card:#0d1020; --card2:#111527;
    --ink:#e8eeff; --muted:#5a6380; --muted2:#3a4260;
    --accent:#5b73ff; --accent-dim:rgba(91,115,255,0.15);
    --cyan:#00e5c8; --amber:#ffb830; --rose:#ff4e6a; --green:#00d68f;
    --border:rgba(91,115,255,0.12); --border2:rgba(255,255,255,0.05);
    --glow:rgba(91,115,255,0.3);
    --font-body:'Space Grotesk',sans-serif;
    --font-mono:'JetBrains Mono',monospace;
    --font-display:'Space Grotesk',sans-serif;
  }
  * { margin:0; padding:0; box-sizing:border-box; }
  .is-bot * { animation: none !important; transition: none !important; scroll-behavior: auto !important; }
  html { scroll-behavior:smooth; }
  body { font-family:var(--font-body); background:var(--bg); color:var(--ink); overflow-x:hidden; line-height:1.6; -webkit-font-smoothing:antialiased; }

  .cursor-glow { position:fixed; width:400px; height:400px; border-radius:50%; background:radial-gradient(circle,rgba(91,115,255,0.06) 0%,transparent 70%); pointer-events:none; z-index:0; top:0; left:0; will-change:transform; transition:transform 0.15s ease; }
  .site-loader {
    position: fixed;
    inset: 0;
    z-index: 5000;
    display: grid;
    place-items: center;
    padding: 2rem;
    background:
      radial-gradient(circle at 18% 18%, rgba(0,229,200,0.12), transparent 32%),
      radial-gradient(circle at 82% 24%, rgba(91,115,255,0.18), transparent 34%),
      linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px),
      linear-gradient(145deg, #02040b 0%, #07101c 52%, #02030a 100%);
    background-size: auto, auto, 42px 42px, 42px 42px, auto;
    opacity: 1;
    visibility: visible;
    transition: opacity .55s ease, visibility .55s ease;
    overflow: hidden;
  }
  .site-loader.done {
    pointer-events: none;
    animation: siteLoaderSwipeUp 1s cubic-bezier(.16, 1, .3, 1) forwards;
  }
  @keyframes siteLoaderSwipeUp {
    0% { transform: translateY(0); }
    100% { transform: translateY(-100vh); visibility: hidden; }
  }
  .site-loader::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      repeating-linear-gradient(0deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 5px),
      linear-gradient(180deg, transparent 0%, rgba(0,229,200,0.10) 48%, transparent 56%);
    mix-blend-mode: screen;
    opacity: .28;
    animation: loaderScan 6s linear infinite;
    pointer-events: none;
  }
  .site-loader::after {
    content: "";
    position: absolute;
    inset: auto 7vw 8vh 7vw;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0,229,200,0.5), transparent);
    box-shadow: 0 0 34px rgba(0,229,200,0.22);
    pointer-events: none;
  }
  .loader-progress-top {
    position: absolute;
    left: 0;
    top: 0;
    z-index: 5;
    width: 100%;
    height: 1px;
    background: rgba(255,255,255,0.08);
    opacity: 0;
    animation: loaderTopReveal .8s ease 8.8s forwards;
  }
  .loader-progress-top span {
    display: block;
    width: 0%;
    height: 100%;
    background: linear-gradient(90deg, #1f7aff, #9a4dff, #f35cff);
    box-shadow: 0 0 18px rgba(154,77,255,0.62);
    transition: width 9.5s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .loader-progress-top.active span {
    width: 100%;
  }
  .loader-split {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .loader-split span {
    background:
      radial-gradient(circle at 50% 45%, rgba(154,77,255,0.12), transparent 38%),
      rgba(2,4,11,0.92);
    backdrop-filter: blur(18px);
    transform: translateX(0);
  }
  .loader-progress-top,
  .loader-intro,
  .loader-card {
    z-index: 2;
  }
  .loader-intro {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: grid;
    place-items: center;
    pointer-events: none;
  }
  .loader-phrase {
    position: absolute;
    width: min(1100px, 92vw);
    color: #eef6ff;
    font-family: var(--font-display);
    font-size: clamp(2.8rem, 10vw, 8rem);
    font-weight: 900;
    line-height: .88;
    letter-spacing: -0.085em;
    text-align: center;
    text-transform: uppercase;
    text-shadow:
      0 0 28px rgba(0,229,200,0.16),
      0 16px 52px rgba(0,0,0,0.52);
    transform: scale(.9);
    opacity: 0;
  }
  .loader-type {
    display: inline-block;
    width: 0;
    max-width: max-content;
    overflow: hidden;
    white-space: nowrap;
    vertical-align: bottom;
    border-right: .055em solid rgba(0,229,200,0.92);
    filter: drop-shadow(0 0 18px rgba(0,229,200,0.12));
  }
  .loader-type-row {
    display: block;
    margin: 0 auto;
  }
  .loader-phrase.boring .loader-type-row:first-child {
    animation:
      typeBoringOne 1s steps(6, end) .3s forwards,
      caretBlink .6s step-end .3s 5;
  }
  .loader-phrase.boring .loader-type-row:last-child {
    border-right-color: transparent;
    animation:
      typeBoringTwo 2s steps(16, end) 1.5s forwards,
      caretBlink .6s step-end 1.5s 5;
  }
  .loader-phrase.boring {
    animation: boringPhrase 5.5s cubic-bezier(.16, 1, .3, 1) forwards;
  }
  .loader-strike {
    content: "";
    position: absolute;
    top: 50%;
    height: clamp(5px, .7vw, 10px);
    background: linear-gradient(90deg, var(--rose), var(--amber));
    border-radius: 999px;
    box-shadow: 0 0 26px rgba(255,78,106,0.48);
    transform: translateY(-50%) scaleX(0) rotate(-1deg);
    transform-origin: left center;
    animation: boringStrike .8s cubic-bezier(.16, 1, .3, 1) 3.2s forwards;
    pointer-events: none;
  }
  .loader-type-row {
    position: relative;
  }
  .loader-type-row .loader-strike {
    left: -0.03em;
    right: -0.03em;
  }
  .loader-phrase.anymore {
    color: #f7fbff;
    text-shadow:
      0 0 32px rgba(0,229,200,0.28),
      0 18px 56px rgba(0,0,0,0.58);
    animation: anymorePhrase 3.5s cubic-bezier(.16, 1, .3, 1) 5.5s forwards;
  }
  .loader-phrase.anymore .loader-type {
    animation:
      typeAnymore 1.5s steps(11, end) 5.8s forwards,
      caretBlink .6s step-end 5.8s 5;
  }
  .loader-card.minimalist-wave {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: transparent;
    box-shadow: none;
    border: none;
    border-radius: 0;
    position: relative;
    opacity: 0;
    transform: translateY(20px);
    will-change: transform, opacity;
    animation: loaderCardEnter 1.5s cubic-bezier(.16, 1, .3, 1) 8.8s forwards;
  }
  .loader-logo-wave-base {
    position: relative;
    font-family: 'Inter', var(--font-mono), sans-serif;
    font-size: clamp(3rem, 12vw, 15rem);
    font-weight: 900;
    color: #2a2a2a;
    line-height: 1;
    display: inline-block;
    margin-bottom: 0;
    letter-spacing: -0.04em;
    padding-bottom: 1rem;
  }
  .loader-logo-wave-fill {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 400' preserveAspectRatio='none'%3E%3Cpath d='M0,50 Q50,0 100,50 T200,50 L200,400 L0,400 Z' fill='%23ffffff' /%3E%3C/svg%3E");
    background-repeat: repeat-x;
    background-size: 2em 4em;
    background-position-y: 1.25em;
    transition: background-position-y 9.5s cubic-bezier(0.16, 1, 0.3, 1);
    animation: waveAnimX 3.8s linear infinite;
  }
  .loader-logo-wave-fill.active {
    background-position-y: -0.55em;
  }
  @keyframes waveAnimX {
    0% { background-position-x: 0; }
    100% { background-position-x: 2em; }
  }
  .loader-wave-percent {
    position: absolute;
    bottom: -10px;
    right: 0;
    font-family: 'Inter', var(--font-mono), sans-serif;
    font-size: clamp(0.7rem, 2vw, 1.2rem);
    color: #ffffff;
    font-weight: 600;
    letter-spacing: 0.05em;
  }
  .loader-inner { position: relative; z-index: 1; }
  .loader-kicker {
    display: inline-flex;
    gap: .55rem;
    align-items: center;
    justify-content: center;
    color: rgba(224,232,255,0.72);
    font-family: var(--font-mono);
    font-size: .74rem;
    font-weight: 700;
    letter-spacing: .28em;
    text-transform: uppercase;
    margin-bottom: 1.2rem;
    background: rgba(255,255,255,0.045);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 999px;
    padding: .35rem .9rem;
    box-shadow: 0 0 28px rgba(154,77,255,0.10);
  }
  .loader-kicker::before,
  .loader-kicker::after {
    content: "";
    width: 34px;
    height: 1px;
    background: currentColor;
    box-shadow: 0 0 12px currentColor;
  }
  .loader-logo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: .08rem;
    font-family: var(--font-mono);
    font-size: clamp(2.8rem, 9vw, 7rem);
    font-weight: 900;
    line-height: .9;
    letter-spacing: -0.1em;
    margin-bottom: 1rem;
    text-transform: uppercase;
    transform: none;
    filter: none;
    position: relative;
    padding: .08em .12em;
    animation: logoBreathe 3.2s ease-in-out infinite;
  }
  .loader-logo::before {
    content: "";
    position: absolute;
    inset: -10% -6%;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(154,77,255,0.18), transparent 66%);
    filter: blur(18px);
    z-index: -1;
    opacity: .9;
  }
  .loader-logo::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(105deg, transparent 0 32%, rgba(255,255,255,0.88) 45%, transparent 58% 100%);
    transform: translateX(-130%);
    mix-blend-mode: screen;
    animation: logoShimmer 2.8s ease-in-out infinite;
    pointer-events: none;
  }
  .loader-logo .l1 {
    color: rgba(235,241,255,0.28);
    background:
      linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.95)) left bottom / 100% var(--loader-progress, 0%) no-repeat,
      linear-gradient(90deg, #ecf4ff, #b9d0ff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 0 28px rgba(31,122,255,0.22);
  }
  .loader-logo .l2 {
    background:
      linear-gradient(180deg, #9a4dff, #f35cff) left bottom / 100% var(--loader-progress, 0%) no-repeat,
      linear-gradient(90deg, #1f7aff, #9a4dff, #f35cff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .loader-title {
    font-family: var(--font-mono);
    font-size: clamp(.74rem, 2.2vw, .92rem);
    line-height: 1.4;
    letter-spacing: .2em;
    text-transform: uppercase;
    color: rgba(235,241,255,0.86);
    margin-bottom: .65rem;
  }
  .loader-copy {
    color: rgba(224,232,255,0.58);
    font-family: var(--font-mono);
    font-size: .76rem;
    font-weight: 500;
    letter-spacing: .08em;
    margin: 0 auto 1.55rem;
    max-width: 620px;
  }
  .loader-track {
    width: min(760px, 82vw);
    height: 2px;
    border-radius: 0;
    background: rgba(255,255,255,0.10);
    overflow: visible;
    border: none;
    box-shadow: 0 0 26px rgba(0,229,200,0.08);
    margin: 0 auto;
  }
  .loader-fill {
    width: var(--loader-progress, 0%);
    height: 100%;
    border-radius: 0;
    background: linear-gradient(90deg, #1f7aff, #9a4dff, #f35cff);
    box-shadow: 0 0 22px rgba(154,77,255,0.46);
    transition: width 1.2s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
  }
  .loader-fill::after {
    content: "";
    position: absolute;
    right: -6px;
    top: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #f4f0ff;
    box-shadow: 0 0 22px rgba(154,77,255,0.86);
    transform: translateY(-50%);
  }
  .loader-percent {
    font-family: var(--font-mono);
    font-size: clamp(2.8rem, 9vw, 6.4rem);
    font-weight: 800;
    line-height: 1;
    color: rgba(238,246,255,0.92);
    letter-spacing: -0.08em;
    margin-bottom: 1rem;
    text-shadow: 0 0 24px rgba(154,77,255,0.26);
  }
  .loader-status-cycle {
    position: relative;
    height: 1.3rem;
    margin: 0 auto 1.25rem;
    color: rgba(224,232,255,0.68);
    font-family: var(--font-mono);
    font-size: .78rem;
    letter-spacing: .08em;
    text-transform: uppercase;
  }
  .loader-status-cycle span {
    position: absolute;
    inset: 0;
    opacity: 0;
    transform: translateY(8px);
    animation: statusCycle 6s ease-in-out infinite;
  }
  .loader-status-cycle span:nth-child(2) { animation-delay: 2s; }
  .loader-status-cycle span:nth-child(3) { animation-delay: 4s; }
  .loader-meta {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-top: .75rem;
    color: rgba(224,232,255,0.62);
    font-family: var(--font-mono);
    font-size: .66rem;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
  }
  .site-shell.loading {
    filter: saturate(.85);
  }
  @keyframes loaderScan {
    from { background-position: 0 -100vh, 0 -100vh; }
    to { background-position: 0 100vh, 0 100vh; }
  }
  @keyframes loaderTopReveal {
    to { opacity: 1; }
  }
  @keyframes splitLeft {
    to { transform: translateX(-102%); }
  }
  @keyframes splitRight {
    to { transform: translateX(102%); }
  }
  @keyframes logoBreathe {
    0%, 100% { transform: scale(1); filter: drop-shadow(0 0 20px rgba(154,77,255,0.16)); }
    50% { transform: scale(1.035); filter: drop-shadow(0 0 34px rgba(31,122,255,0.22)); }
  }
  @keyframes logoShimmer {
    0%, 38% { transform: translateX(-130%); opacity: 0; }
    48% { opacity: .72; }
    64%, 100% { transform: translateX(130%); opacity: 0; }
  }
  @keyframes statusCycle {
    0%, 8% { opacity: 0; transform: translateY(8px); }
    14%, 30% { opacity: 1; transform: translateY(0); }
    38%, 100% { opacity: 0; transform: translateY(-8px); }
  }
  @keyframes typeBoringOne {
    to { width: 6ch; }
  }
  @keyframes typeBoringTwo {
    to { width: 17.25ch; }
  }
  @keyframes typeAnymore {
    to { width: 11ch; }
  }
  @keyframes caretBlink {
    50% { border-right-color: transparent; }
  }
  @keyframes boringPhrase {
    0% { opacity: 0; transform: translateY(42px) scale(.92); filter: blur(8px); letter-spacing: -0.11em; }
    14%, 70% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); letter-spacing: -0.085em; }
    84% { opacity: .72; transform: translateY(-12px) scale(1.02); filter: blur(2px); }
    100% { opacity: 0; transform: translateY(-92px) scale(1.06); filter: blur(20px); letter-spacing: .015em; }
  }
  @keyframes boringStrike {
    from { transform: translateY(-50%) scaleX(0) rotate(-1deg); }
    to { transform: translateY(-50%) scaleX(1) rotate(-1deg); }
  }
  @keyframes anymorePhrase {
    0% { opacity: 0; transform: translateY(64px) scale(.82); filter: blur(8px); letter-spacing: -0.11em; }
    24%, 70% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); letter-spacing: -0.085em; }
    100% { opacity: 0; transform: translateY(-84px) scale(1.08); filter: blur(20px); letter-spacing: .025em; }
  }
  @keyframes loaderCardEnter {
    to { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
  }
  @keyframes loaderCardSwipeUp {
    from { opacity: 1; transform: translateY(0) rotate(0deg) scale(1); }
    to { opacity: 0; transform: translateY(-110vh) rotate(-2deg) scale(.97); }
  }
  @keyframes loaderGlitch {
    0%, 88%, 100% { transform: translate(0, 0); }
    90% { transform: translate(-2px, 1px); }
    92% { transform: translate(2px, -1px); }
    94% { transform: translate(0, 0); }
  }

  .em-nav { position:fixed; top:1.2rem; left:50%; transform:translateX(-50%); width:calc(100% - 4rem); max-width:860px; z-index:900; display:flex; align-items:center; justify-content:space-between; padding:.65rem 1.4rem; background:rgba(255,255,255,0.06); backdrop-filter:blur(24px) saturate(180%); border:1px solid rgba(255,255,255,0.10); border-radius:100px; box-shadow:0 8px 32px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.10); }
  .em-logo { font-family:var(--font-mono); font-size:1.15rem; font-weight:600; letter-spacing:-0.5px; text-decoration:none; color:var(--ink); display:flex; align-items:center; }
  .em-logo .l1 { color:var(--accent); }
  .em-logo .l2 { color:var(--cyan); }
  .em-nav-links { display:flex; gap:2.5rem; list-style:none; }
  .em-nav-links a {
    color: rgba(234, 240, 255, 0.9);
    text-decoration: none;
    font-size: .82rem;
    font-weight: 500;
    letter-spacing: .3px;
    transition: color .2s;
    font-family: var(--font-mono);
    animation: navReadableShift 8s ease-in-out infinite;
    text-shadow: 0 0 8px rgba(5, 8, 20, 0.55);
  }
  .em-nav-links a:hover { color:var(--ink); }
  .em-nav-r { display:flex; gap:.75rem; align-items:center; }
  .btn-ghost {
    background: transparent;
    color: rgba(234, 240, 255, 0.92);
    border: 1px solid var(--border2);
    padding: .45rem 1.1rem;
    border-radius: 100px;
    font-size: .78rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .2s;
    text-decoration: none;
    display: inline-block;
    font-family: var(--font-body);
    animation: navReadableShift 8s ease-in-out infinite;
    text-shadow: 0 0 8px rgba(5, 8, 20, 0.55);
  }
  .btn-ghost:hover { border-color:var(--accent); color:var(--accent); }
  @keyframes navReadableShift {
    0% { color: rgba(238, 243, 255, 0.9); text-shadow: 0 0 8px rgba(5, 8, 20, 0.55); }
    25% { color: rgba(210, 235, 255, 0.95); text-shadow: 0 0 10px rgba(12, 36, 72, 0.5); }
    50% { color: rgba(224, 255, 244, 0.94); text-shadow: 0 0 11px rgba(9, 36, 32, 0.52); }
    75% { color: rgba(233, 241, 255, 0.96); text-shadow: 0 0 9px rgba(20, 24, 58, 0.5); }
    100% { color: rgba(238, 243, 255, 0.9); text-shadow: 0 0 8px rgba(5, 8, 20, 0.55); }
  }
  .btn-cta { background:var(--accent); color:#fff; border:none; padding:.5rem 1.3rem; border-radius:100px; font-size:.82rem; font-weight:600; cursor:pointer; transition:all .2s; text-decoration:none; display:inline-block; font-family:var(--font-body); position:relative; overflow:hidden; box-shadow:0 0 20px rgba(91,115,255,0.4); }
  .btn-cta:hover { box-shadow:0 0 24px var(--glow); transform:translateY(-1px); }
  .hbg { display:none; flex-direction:column; gap:5px; background:none; border:none; cursor:pointer; padding:4px; }
  .hbg span { display:block; width:22px; height:2px; background:var(--muted); border-radius:2px; transition:all .3s; }
  .hbg.open span:nth-child(1) { transform:translateY(7px) rotate(45deg); }
  .hbg.open span:nth-child(2) { opacity:0; }
  .hbg.open span:nth-child(3) { transform:translateY(-7px) rotate(-45deg); }
  .mob-nav { display:none; position:fixed; inset:0; z-index:850; background:rgba(4,5,10,0.97); backdrop-filter:blur(20px); flex-direction:column; align-items:center; justify-content:center; gap:2.5rem; }
  .mob-nav.open { display:flex; }
  .mob-nav a { font-family:var(--font-display); font-size:2rem; font-weight:700; color:var(--muted); text-decoration:none; letter-spacing:-1px; transition:color .2s; }
  .mob-nav a:hover { color:var(--accent); }

  .em-mob-only { display: none !important; }
  .em-hero {
    min-height: 100vh;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 0;
  }
  .hero-bg-image {
    position: absolute;
    inset: 0;
    background-image: url('/assets/Firefly.png');
    background-size: cover;
    background-position: center;
    z-index: 0;
  }
  .hero-content-wrapper {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
    padding: 10rem 4rem 2rem 4rem;
  }
  .hero-left {
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 600px;
  }
  .hero-right {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 0;
    margin-top: -3rem;
  }
  .hero-trust-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.4rem 1rem;
    border-radius: 100px;
    border: 1px solid rgba(31, 122, 255, 0.4);
    background: rgba(4, 6, 15, 0.6);
    color: #a0abbf;
    font-family: var(--font-body);
    font-size: 0.85rem;
    width: fit-content;
    margin-bottom: 2rem;
    backdrop-filter: blur(8px);
  }
  .hero-trust-badge svg {
    width: 16px;
    height: 16px;
    stroke: #1f7aff;
  }
  .hero-main-title {
    font-family: var(--font-display);
    font-size: 5.5rem;
    font-weight: 800;
    line-height: 1.05;
    letter-spacing: -2px;
    color: #ffffff;
    margin-bottom: 1.5rem;
  }
  .hero-main-title .text-grad {
    background: linear-gradient(110deg, #1f7aff 0%, #00e5c8 35%, #9a4dff 68%, #ff4e6a 100%);
    background-size: 220% 220%;
    animation: heroGradientFlow 6s ease-in-out infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  @keyframes heroGradientFlow {
    0% { background-position: 0% 50%; filter: hue-rotate(0deg); }
    50% { background-position: 100% 50%; filter: hue-rotate(18deg); }
    100% { background-position: 0% 50%; filter: hue-rotate(0deg); }
  }
  .hero-description {
    font-family: var(--font-body);
    font-size: 1.1rem;
    color: #a0abbf;
    line-height: 1.6;
    max-width: 480px;
  }
  .hero-features-list {
    display: flex;
    flex-direction: column;
    gap: 1.8rem;
    align-items: flex-start;
  }
  .hf-item {
    display: flex;
    align-items: center;
    gap: 1.2rem;
  }
  .hf-icon {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .hf-icon svg {
    width: 32px;
    height: 32px;
    stroke: #1f7aff;
  }
  .hf-text {
    display: flex;
    flex-direction: column;
  }
  .hf-text h4 {
    font-family: var(--font-body);
    font-size: 1rem;
    font-weight: 700;
    color: #1f7aff;
    letter-spacing: 0.5px;
    margin-bottom: 0.2rem;
    text-transform: uppercase;
  }
  .hf-text p {
    font-family: var(--font-body);
    font-size: 0.85rem;
    color: #a0abbf;
  }
  .hero-bottom-bar {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 4rem 3rem 4rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  .hero-stats-row {
    display: flex;
    gap: 3rem;
  }
  .hs-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .hs-val {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
  }
  .hs-val svg {
    stroke: #1f7aff;
  }
  .hs-lbl {
    font-family: var(--font-body);
    font-size: 0.85rem;
    color: #a0abbf;
    line-height: 1.4;
  }
  .hero-actions-row {
    display: flex;
    gap: 1rem;
  }
  .btn-solid-blue {
    background: #1f7aff;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    padding: 0.9rem 1.8rem;
    font-family: var(--font-body);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
  }
  .btn-solid-blue:hover {
    background: #1660d9;
  }
  .btn-outline-dark {
    background: rgba(0, 0, 0, 0.4);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 0.9rem 1.8rem;
    font-family: var(--font-body);
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    backdrop-filter: blur(8px);
  }
  .btn-outline-dark:hover {
    border-color: #ffffff;
    background: rgba(0, 0, 0, 0.6);
  }

  .ticker { background:var(--bg2); border-top:1px solid var(--border2); border-bottom:1px solid var(--border2); padding:.7rem 0; overflow:hidden; }
  .tk-track { display:flex; gap:2.5rem; animation:tkScroll 28s linear infinite; white-space:nowrap; }
  .tk-track span { font-family:var(--font-mono); font-size:.65rem; font-weight:600; letter-spacing:3px; text-transform:uppercase; color:#1a2040; flex-shrink:0; }
  .tk-track span.hi { color:var(--accent); }
  @keyframes tkScroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
  .tk2 { background:none; border-top:none; padding:.5rem 0; }
  .tk-track2 { animation:tkScroll2 22s linear infinite; }
  @keyframes tkScroll2 { 0%{transform:translateX(-50%)} 100%{transform:translateX(0)} }

  .em-section { padding:5.5rem 5rem; }
  .with-waves { position: relative; overflow: hidden; }
  .section-waves {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    opacity: .6;
  }
  .section-waves.review-waves {
    opacity: .72;
    mix-blend-mode: normal;
  }
  .with-waves > :not(.section-waves) {
    position: relative;
    z-index: 1;
  }
  .center { text-align:center; }
  .tag {
    display:inline-block;
    font-family:var(--font-mono);
    font-size:.63rem;
    color:var(--accent);
    background:var(--accent-dim);
    border:1px solid rgba(91,115,255,0.2);
    padding:.25rem .75rem;
    border-radius:100px;
    letter-spacing:1.5px;
    margin-bottom:1rem;
    text-transform:uppercase;
  }
  .tag.cyan { color:var(--cyan); background:rgba(0,229,200,0.08); border-color:rgba(0,229,200,0.2); }
  .tag.amber { color:var(--amber); background:rgba(255,184,48,0.08); border-color:rgba(255,184,48,0.2); }
  .tag.green { color:var(--green); background:rgba(0,214,143,0.08); border-color:rgba(0,214,143,0.2); }
  .sh {
    font-family:var(--font-display);
    font-size:2.6rem;
    font-weight:700;
    letter-spacing:-1.2px;
    line-height:1.1;
    margin-bottom:.7rem;
  }
  .sh .g {
    background: linear-gradient(90deg, var(--accent), var(--cyan));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .sh .ga {
    background: linear-gradient(90deg, var(--amber), var(--rose));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .sub {
    color:var(--muted);
    font-size:.9rem;
    line-height:1.75;
    max-width:500px;
  }

  .feature-showcase {
    background:
      radial-gradient(120% 90% at 50% 0%, rgba(91,115,255,0.14) 0%, rgba(91,115,255,0.02) 45%, transparent 75%),
      var(--bg);
    color: var(--ink);
    text-align: center;
    position: relative;
    overflow: hidden;
    border-top: 1px solid var(--border2);
    border-bottom: 1px solid var(--border2);
  }
  .feature-showcase-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    opacity: 0.55;
  }
  .feature-wrap { position: relative; z-index: 1; max-width: 1360px; margin: 0 auto; }
  .feature-headline { position: relative; display: inline-block; padding-top: .55rem; }
  .feature-marquee {
    position: absolute;
    left: 50%;
    top: 52%;
    transform: translate(-50%, -50%);
    width: 100vw;
    height: clamp(3.2rem, 9vw, 6.4rem);
    overflow: hidden;
    pointer-events: none;
    z-index: 0;
  }
  .feature-marquee-track {
    position: absolute;
    left: 0;
    top: 0;
    display: flex;
    width: max-content;
    white-space: nowrap;
    animation: featureMarquee 20s linear infinite;
  }
  .feature-marquee-track span {
    font-size: clamp(2.5rem, 8vw, 5.4rem);
    line-height: 1.18;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: rgba(0, 229, 200, 0.12);
    flex: 0 0 auto;
    padding-right: 2rem;
    user-select: none;
  }
  @keyframes featureMarquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .feature-tagline {
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(2.1rem, 5vw, 4rem);
    line-height: 1.08;
    letter-spacing: -0.02em;
    margin-top: 1rem;
    color: var(--ink);
    font-style: italic;
    font-weight: 700;
    position: relative;
    z-index: 2;
  }
  .feature-tagline .blew { color: blue; }
  .presence-title .blew { color: blue; }
  .feature-tagline .gold { color: #ffad00; }
  .feature-subline {
    margin: 1.35rem auto 0;
    max-width: 800px;
    color: var(--muted);
    font-size: 1.05rem;
    line-height: 1.7;
  }
  .feature-mini {
    margin-top: 2.2rem;
    display: inline-flex;
    gap: 1.2rem;
    align-items: center;
    color: rgba(232,238,255,0.78);
    font-family: var(--font-mono);
    font-size: 0.78rem;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }
  .feature-mini-item {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
  }
  .feature-mini-icon1 {
    width: 27px;
    height: 27px;
    object-fit: contain;
    display: block;
  }
  .feature-mini-icon2 {
    width: 22px;
    height: 22px;
    object-fit: contain;
    display: block;
  }
  .feature-mini .dotline {
    width: 1px;
    height: 14px;
    background: white;
  }
  .feature-cards-layout {
    margin-top: 2.7rem;
    display: grid;
    grid-template-columns: 430px 430px 450px;
    gap: 1.15rem;
    align-items: stretch;
    justify-content: center;
  }
  .feature-cards-layout > .pcard:nth-child(1) { grid-column: 1; grid-row: 1 / span 2; }
  .feature-cards-layout > .pcard:nth-child(2) { grid-column: 2; grid-row: 1 / span 2; }
  .feature-cards-layout > .pcard:nth-child(3) { grid-column: 3; grid-row: 1; }
  .feature-cards-layout > .pcard:nth-child(4) { grid-column: 3; grid-row: 2; }
  .pcard {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 22px;
    border: 1px solid rgba(255,255,255,0.34);
    background: linear-gradient(165deg, rgba(13,16,32,0.96), rgba(4,5,10,0.96));
    box-shadow:
      0 10px 26px rgba(2, 6, 20, 0.32),
      0 0 14px rgba(255, 255, 255, 0.07);
    transition: transform .28s ease, box-shadow .28s ease, border-color .28s ease;
  }
  .pcard::before {
    content: "";
    position: absolute;
    inset: -10px;
    border-radius: 28px;
    background: radial-gradient(70% 65% at 50% 45%, rgba(183, 210, 255, 0.22) 0%, rgba(183, 210, 255, 0.0) 72%);
    filter: blur(12px);
    opacity: 0.42;
    z-index: -1;
    pointer-events: none;
    transition: opacity .28s ease, filter .28s ease;
  }
  .pcard:hover {
    transform: translateY(-8px) scale(1.01);
    border-color: rgba(255,255,255,0.62);
    box-shadow:
      0 18px 40px rgba(2, 6, 20, 0.5),
      0 0 30px rgba(189, 220, 255, 0.2),
      0 0 56px rgba(134, 183, 255, 0.14);
  }
  .pcard:hover::before {
    opacity: 0.78;
    filter: blur(16px);
  }
  .pcard .pixel-canvas {
    position: absolute;
    inset: 0;
    z-index: 0;
  }
  .pcard-spline-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    display: block;
    transform: translateY(18%) scale(1.18);
    transform-origin: center top;
    z-index: 1;
    pointer-events: auto;
  }
  .pcard-with-spline .pixel-canvas {
    z-index: 0;
  }
  .pcard-with-spline .pcard-content {
    pointer-events: none;
  }
  .pcard-with-spline .pcard-btn {
    pointer-events: auto;
  }
  .pcard-sketchfab-wrapper {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    overflow: hidden;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(11, 14, 28, 0.96), rgba(28, 12, 36, 0.9));
  }
  .pcard-sketchfab-wrapper iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
 .pcard-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    z-index: 1;
    pointer-events: none;
  }
  .pcard-content {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    text-align: right;
    padding: 1.55rem;
    color: #f5f7fb;
    pointer-events: auto;
  }
  .pcard-content-left {
    align-items: flex-start;
    text-align: left;
  }
  .pcard-title {
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(1.45rem, 2.1vw, 2.2rem);
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -0.02em;
  }
  .pcard-btn {
    margin-top: 0.95rem;
    border: 1px solid rgba(232,238,255,0.35);
    border-radius: 100px;
    padding: 0.38rem 0.85rem;
    color: #ffffff;
    font-size: 0.82rem;
    font-weight: 600;
    background: rgba(5, 8, 20, 0.34);
    font-family: var(--font-body);
    cursor: pointer;
    pointer-events: auto;
    transition: border-color .2s ease, color .2s ease, background .2s ease;
  }
  .pcard-btn:hover {
    border-color: rgba(0,229,200,0.7);
    color: var(--cyan);
    background: rgba(4, 8, 22, 0.75);
  }
  .pcard-art {
    margin-top: auto;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
  }
  .pcard-art.left { justify-content: flex-start; }
  .pcard-art.center { justify-content: center; }
  .pcard-art span {
    font-size: clamp(4rem, 7vw, 9rem);
    opacity: 0.92;
    line-height: 0.9;
  }
  .pcard.tall { width: 410px; height: 500px; min-height: 500px; }
  .pcard.short { width: 430px; height: 238px; min-height: 238px; }

  .presence-sec {
    position: relative;
    overflow: hidden;
    contain: layout paint;
    background:
      radial-gradient(120% 100% at 50% -10%, rgba(255, 184, 48, 0.18) 0%, rgba(255, 184, 48, 0.04) 36%, transparent 72%),
      linear-gradient(180deg, #0b0e1b 0%, #070910 100%);
    border-top: 1px solid var(--border2);
    border-bottom: 1px solid var(--border2);
  }
  .community-sec {
    position: relative;
    overflow: hidden;
    min-height: 0;
    padding: 4.75rem 3.25rem 5rem;
    border-top: 1px solid var(--border2);
      text-align: center;
    background: linear-gradient(180deg, #06070f 0%, #05060b 100%);
  }
  .community-shell {
    position: relative;
    width: min(1680px, 100%);
    margin: 0 auto;
    min-height: 520px;
    border-radius: 32px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.41);
    box-shadow: 0 26px 80px rgba(0,0,0,0.42);
    background: #05060b;
  }
  .community-bends {
    position: absolute;
    inset: 0;
  }
  .community-overlay {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(70% 55% at 50% 42%, rgba(4,6,10,0.06) 0%, rgba(4,6,10,0.34) 58%, rgba(4,6,10,0.78) 100%),
      linear-gradient(180deg, rgba(4,5,10,0.1) 0%, rgba(4,5,10,0.48) 100%);
    z-index: 1;
    pointer-events: none;
  }
  .community-copy {
    position: relative;
    z-index: 2;
    min-height: 520px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 2rem;
  }
  .community-copy-inner {
    width: min(920px, 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.15rem;
    color: #fff;
  }
.community-pill {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    padding: .38rem .85rem;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.22);
    background: rgba(8, 10, 18, 0.72);
    font-family: var(--font-mono);
    font-size: .65rem;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: rgba(255,255,255,0.92);
  }
  .community-lead {
    max-width: 760px;
    font-size: clamp(.92rem, 1.4vw, 1.15rem);
    line-height: 1.8;
    color: rgba(245,248,255,0.88);
  }
  .community-count {
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(3.2rem, 8.2vw, 6.8rem);
    line-height: 1.08;
    letter-spacing: -0.05em;
    font-style: italic;
    margin: .2rem 0;
    white-space: nowrap;
    overflow: visible;
    text-shadow: 0 0 24px rgba(255,255,255,0.12);
  }
  .community-count span {
    display: inline-block;
    padding: .04em .08em .08em;
    background: linear-gradient(90deg, #ffffff 0%, #fff1cc 28%, #b4f5ff 58%, #ffffff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .community-sub {
    max-width: 650px;
    font-size: .86rem;
    line-height: 1.8;
    color: rgba(232,238,255,0.72);
  }
  .community-stats {
    display: flex;
    gap: .75rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: .5rem;
  }
  .community-stats-track {
    display: flex;
    gap: .75rem;
    justify-content: center;
    flex-wrap: wrap;
  }
.community-stat {
    min-width: 150px;
    padding: .85rem 1rem;
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,0.14);
    background: rgba(5, 8, 16, 0.72);
  }
  .community-stat strong {
    display: block;
    font-family: var(--font-display);
    font-size: 1.1rem;
    color: #ffffff;
    margin-bottom: .2rem;
  }
  .community-stat span {
    font-size: .68rem;
    color: rgba(232,238,255,0.7);
    letter-spacing: .35px;
  }
  @keyframes communityStatSingleMobile {
    0%, 4% {
      opacity: 0;
      transform: translateX(8%);
    }
    7%, 31% {
      opacity: 1;
      transform: translateX(0%);
    }
    35%, 100% {
      opacity: 0;
      transform: translateX(-8%);
    }
  }
  .presence-head {
    position: relative;
    z-index: 2;
    max-width: 980px;
    margin: 0 auto;
    text-align: center;
  }
  .presence-line {
    display: flex;
    align-items: center;
    gap: .75rem;
    margin-bottom: 1.1rem;
    width: 100%;
    padding: 0 1.2rem;
  }
  .presence-line::before,
  .presence-line::after {
    content: "";
    height: 1px;
    flex: 1;
    border-radius: 999px;
    background: linear-gradient(90deg, transparent 0%, rgba(255,184,48,0.75) 50%, rgba(255,238,190,0.68) 100%);
  }
  .presence-line::after {
    background: linear-gradient(90deg, rgba(255,238,190,0.68) 0%, rgba(255,184,48,0.75) 50%, transparent 100%);
  }
  .presence-pill {
    flex-shrink: 0;
    font-family: var(--font-mono);
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: #ffd878;
    background: linear-gradient(180deg, rgba(255,184,48,0.22), rgba(255,184,48,0.06));
    border: 1px solid rgba(255, 184, 48, 0.35);
    border-radius: 999px;
    padding: .42rem .95rem;
  }
  .presence-sec {
    position: relative;
    padding: 6rem 0;
    overflow: hidden;
  }
  .presence-bg {
    position: absolute;
    left: 0;
    right: 0;
    top: 3.9rem;
    height: clamp(4.5rem, 9vw, 7.4rem);
    overflow: hidden;
    pointer-events: none;
    z-index: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
.presence-bg-track {
    display: flex;
    width: max-content;
    white-space: nowrap;
    animation: presenceBgScroll 240s linear infinite;
  }
  .presence-bg-group {
    display: flex;
    flex-shrink: 0;
    gap: 2.4rem;
    padding: 0 1.2rem 0 0;
  }
  .presence-bg-track span {
    font-family: var(--font-display);
    font-size: clamp(3.2rem, 10vw, 7.6rem);
    font-weight: 700;
    letter-spacing: .11em;
    color: rgba(255, 230, 178, 0.14);
    text-transform: uppercase;
    padding-right: 0;
  }
  @keyframes presenceBgScroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  .presence-title {
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(1.85rem, 4vw, 3.4rem);
    line-height: 1.12;
    letter-spacing: -0.02em;
    color: #fef4df;
    font-style: italic;
    margin-bottom: .65rem;
    position: relative;
    z-index: 2;
  }
  .presence-title .presence-engi {
    color: var(--accent);
    font-style: normal;
    font-weight: 700;
  }
  .presence-sub {
    color: rgba(232,238,255,0.78);
    max-width: 780px;
    margin: 0 auto;
    font-size: .96rem;
    line-height: 1.75;
    position: relative;
    z-index: 2;
  }
  .presence-rows {
    margin-top: 2.4rem;
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    position: relative;
    z-index: 2;
    width: 100%;
    pointer-events: none;
  }
  .presence-row {
    overflow: hidden;
    width: 100%;
    position: relative;
  }
  .presence-row::before,
  .presence-row::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: clamp(40px, 7vw, 120px);
    z-index: 3;
    pointer-events: none;
  }
  .presence-row::before {
    left: 0;
    background: linear-gradient(90deg, #070910 0%, rgba(7,9,16,0.92) 35%, rgba(7,9,16,0) 100%);
  }
  .presence-row::after {
    right: 0;
    background: linear-gradient(270deg, #070910 0%, rgba(7,9,16,0.92) 35%, rgba(7,9,16,0) 100%);
  }
  .presence-track {
    display: flex;
    width: max-content;
    gap: 0;
    animation: presenceLogoScroll 345s linear infinite;
    pointer-events: none;
  }
  .presence-group {
    display: flex;
    flex-shrink: 0;
    gap: 1rem;
    padding: 0 .5rem;
    /* Both groups must be pixel-identical — no asymmetric padding */
  }
  .presence-track.rev {
    animation-direction: reverse;
  }
  @keyframes presenceLogoScroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
  .presence-card {
    width: 148px;
    flex-shrink: 0;
    border: 1px solid rgba(255, 184, 48, 0.18);
    background: linear-gradient(170deg, rgba(20,24,40,0.9), rgba(10,12,22,0.92));
    border-radius: 14px;
    padding: .6rem .55rem .72rem;
  }

  .presence-logo-wrap {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 10px;
    overflow: hidden;
    background: rgba(255,255,255,0.96);
    padding: .52rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .presence-logo-wrap img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  .presence-name {
    margin-top: .55rem;
    font-size: .62rem;
    line-height: 1.35;
    color: rgba(232,238,255,0.82);
    text-align: center;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .roadmap-grid-sec {
    background:
      radial-gradient(110% 100% at 50% 0%, rgba(91,115,255,0.16) 0%, rgba(91,115,255,0.04) 38%, transparent 75%),
      linear-gradient(180deg, var(--bg2) 0%, var(--bg) 100%);
    border-top: 1px solid var(--border2);
    border-bottom: 1px solid var(--border2);
    overflow: hidden;
  }
  .roadmap-grid-head {
    text-align: center;
    max-width: 860px;
    margin: 0 auto 2.2rem;
  }
  .roadmap-grid-head .sub {
    margin: 0 auto;
  }
  .roadmap-grid-wrap {
    position: relative;
    width: 100%;
    height: min(68vh, 660px);
    min-height: 420px;
    border-radius: 18px;
    border: 1px solid rgba(255,255,255,0.09);
    background: linear-gradient(180deg, rgba(13,16,32,0.54), rgba(5,8,20,0.72));
    box-shadow: 0 16px 44px rgba(0,0,0,0.35);
    overflow: hidden;
  }

  .crs-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1.2rem; margin-top:1.2rem; }
  .crs-card { background:var(--card); border:1px solid var(--border2); border-radius:14px; overflow:hidden; transition:all .3s; text-decoration:none; color:inherit; display:block; position:relative; }
  .crs-card:hover { border-color:var(--accent); transform:translateY(-5px); box-shadow:0 16px 48px rgba(91,115,255,0.15); }
  .crs-badge { position:absolute; top:.8rem; right:.8rem; font-family:var(--font-mono); font-size:.58rem; font-weight:600; padding:.2rem .6rem; border-radius:100px; letter-spacing:.5px; }
  .badge-hot{background:rgba(255,78,106,0.9);color:#fff;} .badge-new{background:rgba(0,229,200,0.9);color:#04050a;} .badge-free{background:rgba(0,214,143,0.9);color:#04050a;}
  .crs-top { height:140px; display:flex; align-items:center; justify-content:center; font-size:3.5rem; position:relative; overflow:hidden; }
  .crs-bg-c{background:linear-gradient(135deg,rgba(91,115,255,0.15),rgba(0,229,200,0.08));} .crs-bg-m{background:linear-gradient(135deg,rgba(255,184,48,0.12),rgba(255,78,106,0.08));} .crs-bg-p{background:linear-gradient(135deg,rgba(0,214,143,0.12),rgba(91,115,255,0.08));} .crs-bg-e{background:linear-gradient(135deg,rgba(255,78,106,0.1),rgba(255,184,48,0.08));}
  .crs-body { padding:1.3rem; }
  .crs-cat { font-family:var(--font-mono); font-size:.6rem; color:var(--muted); letter-spacing:1px; text-transform:uppercase; margin-bottom:.4rem; }
  .crs-title { font-family:var(--font-display); font-size:.95rem; font-weight:600; margin-bottom:.5rem; }
  .crs-meta { display:flex; gap:.8rem; font-family:var(--font-mono); font-size:.65rem; color:var(--muted); }
  .crs-footer { display:flex; align-items:center; justify-content:space-between; padding:.8rem 1.3rem; border-top:1px solid var(--border2); }
  .crs-price { font-family:var(--font-display); font-size:1.1rem; font-weight:700; color:var(--accent); }
  .crs-action { font-family:var(--font-mono); font-size:.65rem; color:var(--muted); }
  .crs-card:hover .crs-action { color:var(--cyan); }

  .c-spotlight { background:linear-gradient(120deg,var(--bg2) 0%,rgba(91,115,255,0.05) 100%); border:1px solid var(--border); border-radius:20px; padding:2.5rem; margin-top:3rem; display:grid; grid-template-columns:1fr 1fr; gap:2.5rem; align-items:center; }
  .cs-tag { font-family:var(--font-mono); font-size:.6rem; color:var(--cyan); letter-spacing:2px; text-transform:uppercase; margin-bottom:.6rem; }
  .cs-left h3, .cs-left h2 { font-family:var(--font-display); font-size:1.6rem; font-weight:700; letter-spacing:-.5px; margin-bottom:.6rem; }
  .cs-left p { color:var(--muted); font-size:.85rem; line-height:1.75; }
  .cs-feats { list-style:none; margin-top:1.2rem; }
  .cs-feats li { padding:.55rem 0; border-bottom:1px solid var(--border2); font-size:.82rem; color:var(--muted); display:flex; gap:.6rem; align-items:flex-start; }
  .cs-feats li:last-child { border:none; }
  .cs-feats .ck { color:var(--cyan); font-weight:700; flex-shrink:0; }
  .cs-right { background:var(--bg); border:1px solid var(--border2); border-radius:14px; padding:1.4rem; font-family:var(--font-mono); font-size:.72rem; }
  .cr-bar { display:flex; align-items:center; gap:.5rem; margin-bottom:1rem; color:var(--muted2); font-size:.6rem; letter-spacing:2px; }
  .cr-dot { width:8px; height:8px; border-radius:50%; }
  .cr-r{background:#ff5f56;} .cr-y{background:#ffbd2e;} .cr-g{background:#27c93f;}
  .code-line { padding:.2rem 0; line-height:1.8; }
  .cl-com{color:#2a3560;} .cl-kw{color:var(--rose);} .cl-fn{color:var(--accent);} .cl-str{color:var(--green);} .cl-num{color:var(--amber);}
  .cl-out { color:var(--cyan); margin-top:.4rem; display:block; padding-top:.4rem; border-top:1px solid var(--border2); }
  .cs-btns { display:flex; gap:.7rem; margin-top:1.5rem; flex-wrap:wrap; }
  .cs-btns .btn-primary { font-size:.85rem; padding:.7rem 1.6rem; }
  .cs-btns .btn-secondary { font-size:.85rem; padding:.7rem 1.6rem; }

  .notes-sec { background:var(--bg2); }
  .notes-layout { display:grid; grid-template-columns:1fr 1.6fr; gap:3rem; align-items:start; margin-top:3rem; }
  .nl-left { position:sticky; top:6rem; }
  .nl-checklist { list-style:none; margin-top:1.5rem; }
  .nl-checklist li { display:flex; align-items:center; gap:.7rem; color:var(--muted); font-size:.85rem; padding:.45rem 0; }
  .nl-checklist .ck { color:var(--green); font-weight:700; }
  .nl-right { display:flex; flex-direction:column; gap:.8rem; }
  .note-item { display:flex; align-items:center; gap:1.2rem; background:var(--card); border:1px solid var(--border2); border-radius:12px; padding:1.1rem 1.4rem; text-decoration:none; color:inherit; transition:all .3s; }
  .note-item:hover { border-color:var(--accent); transform:translateX(6px); }
  .ni-ico { width:40px; height:40px; flex-shrink:0; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:1.1rem; }
  .note-info { flex:1; }
  .note-title { font-family:var(--font-display); font-size:.9rem; font-weight:600; margin-bottom:.2rem; }
  .note-desc { font-size:.75rem; color:var(--muted); }
  .note-badge-s { font-family:var(--font-mono); font-size:.58rem; padding:.15rem .55rem; border-radius:100px; white-space:nowrap; }
  .nb-free{background:rgba(0,214,143,0.1);color:var(--green);border:1px solid rgba(0,214,143,0.2);} .nb-pop{background:rgba(91,115,255,0.1);color:var(--accent);border:1px solid rgba(91,115,255,0.2);} .nb-new{background:rgba(0,229,200,0.1);color:var(--cyan);border:1px solid rgba(0,229,200,0.2);} .nb-top{background:rgba(255,184,48,0.1);color:var(--amber);border:1px solid rgba(255,184,48,0.2);}
  .note-arrow { color:var(--muted2); font-size:1rem; transition:all .3s; }
  .note-item:hover .note-arrow { color:var(--accent); transform:translateX(3px); }

  .mpq-sec { background:var(--bg); }
  .mpq-layout { display:grid; grid-template-columns:1fr 1fr; gap:4rem; align-items:center; margin-top:3rem; }
  .mpq-list { list-style:none; margin-top:1.5rem; }
  .mpq-list li { padding:.7rem 0; border-bottom:1px solid var(--border2); color:var(--muted); font-size:.875rem; display:flex; align-items:center; gap:.65rem; }
  .mpq-list li:last-child { border:none; }
  .mpq-list .ck { color:var(--green); font-weight:700; }
  .mpq-demo { background:var(--card); border:1px solid var(--border); border-radius:16px; padding:1.8rem; position:relative; overflow:hidden; }
  .mpq-subject { font-family:var(--font-mono); font-size:.6rem; color:var(--muted); letter-spacing:1.5px; text-transform:uppercase; margin-bottom:.8rem; }
  .mpq-q { font-size:.875rem; font-weight:600; line-height:1.65; margin-bottom:1rem; }
  .mpq-q code { font-family:var(--font-mono); font-size:.78rem; color:var(--cyan); background:rgba(0,229,200,0.06); padding:.3rem .5rem; border-radius:4px; display:block; line-height:1.8; margin-top:.5rem; }
  .mpq-opts { display:flex; flex-direction:column; gap:.5rem; }
  .mpq-opt { background:rgba(91,115,255,0.04); border:1px solid rgba(91,115,255,0.12); border-radius:7px; padding:.55rem .85rem; font-size:.78rem; cursor:pointer; transition:all .2s; color:var(--muted); text-align:left; }
  .mpq-opt:hover { background:rgba(91,115,255,0.1); border-color:var(--accent); color:var(--ink); }
  .mpq-opt.correct { background:rgba(0,214,143,0.1); border-color:var(--green); color:var(--green); }
  .mpq-opt.wrong { background:rgba(255,78,106,0.08); border-color:var(--rose); color:var(--rose); }
  .mpq-meta-row { display:flex; gap:1.2rem; margin-top:1rem; font-family:var(--font-mono); font-size:.65rem; color:var(--muted); }

  .testi-sec {
    background: transparent;
    border-top: 1px solid var(--border2);
    border-bottom: 1px solid var(--border2);
    padding: 5.2rem 0;
    overflow: hidden;
    position: relative;
  }
  .testi-sec::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.72) 8%, rgba(0,0,0,0) 20%),
      linear-gradient(0deg, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.72) 8%, rgba(0,0,0,0) 20%);
    z-index: 1;
    pointer-events: none;
  }
  .review-shell {
    width: min(1440px, 100%);
    margin: 0 auto;
    padding: 0 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    position: relative;
    z-index: 2;
  }
  .review-head {
    position: relative;
    z-index: 1;
    text-align: center;
    padding: .9rem 0 0;
  }
  .review-head::before {
    content: none;
  }
  .review-line {
    display: flex;
    align-items: center;
    gap: .6rem;
    width: 100%;
    margin-bottom: 1rem;
  }
  .review-line::before,
  .review-line::after {
    content: "";
    flex: 1;
    height: 1px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(255,255,255,0.88) 0%, rgba(180,180,180,0.45) 55%, rgba(0,0,0,0) 100%);
  }
  .review-line::after {
    background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(180,180,180,0.45) 45%, rgba(255,255,255,0.88) 100%);
  }
  .review-pill {
    flex-shrink: 0;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(10, 10, 10, 0.9);
    color: rgba(255,255,255,0.82);
    font-family: var(--font-mono);
    font-size: .75rem;
    letter-spacing: 1.6px;
    font-weight: 700;
    text-transform: uppercase;
    padding: .35rem .95rem;
  }
  .review-bg-marquee {
    position: absolute;
    left: 0;
    right: 0;
    top: .2rem;
    overflow: hidden;
    pointer-events: none;
  }
  .review-bg-track {
    display: flex;
    width: max-content;
    white-space: nowrap;
    animation: reviewBgMove 40s linear infinite;
  }
  .review-bg-track span {
    display: inline-flex;
    align-items: center;
    padding-right: 1.4rem;
    font-size: clamp(2.2rem, 8vw, 5.8rem);
    font-weight: 800;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.1);
    text-shadow: none;
    letter-spacing: .06em;
    user-select: none;
  }
  @keyframes reviewBgMove {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .review-title {
    position: relative;
    z-index: 1;
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(2rem, 4vw, 3.4rem);
    color: #fff4dc;
    font-style: italic;
    letter-spacing: -0.02em;
    margin-bottom: .5rem;
  }
  .review-sub {
    position: relative;
    z-index: 1;
    color: rgba(232,238,255,0.75);
    font-size: .96rem;
    max-width: 720px;
    margin: 0 auto;
  }
  .review-ratings {
    display: flex;
    gap: .8rem;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
  .review-rate-card {
    display: inline-flex;
    align-items: center;
    gap: .6rem;
    border-radius: 12px;
    border: 1px solid rgba(255, 184, 48, 0.45);
    background: rgba(255,255,255,0.97);
    color: #222;
    padding: .6rem .8rem;
    min-width: 170px;
    box-shadow: 0 8px 20px rgba(0,0,0,0.2);
  }
  .review-rate-icon {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .review-rate-icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  .review-rate-copy small {
    display: block;
    font-size: .62rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    color: #697081;
  }
  .review-rate-copy strong {
    font-size: .82rem;
    color: #1d2433;
  }
  .review-columns {
    position: relative;
    display: grid;
    grid-template-columns: repeat(4, minmax(220px, 1fr));
    gap: .95rem;
    max-height: min(68vh, 700px);
    overflow: hidden;
    padding: .3rem 0;
  }
  .review-columns::before,
  .review-columns::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 70px;
    z-index: 5;
    pointer-events: none;
  }
  .review-columns::before {
    top: 0;
    background: linear-gradient(180deg, rgba(6,8,16,0) 0%, rgba(6,8,16,0) 100%);
  }
  .review-columns::after {
    bottom: 0;
    background: linear-gradient(0deg, rgba(6,8,16,0.98) 0%, rgba(6,8,16,0) 100%);
  }
  .review-columns-curve {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: min(1200px, calc(100% - 1.2rem));
    height: 92px;
    z-index: 6;
    pointer-events: none;
    overflow: hidden;
  }
  .review-columns-curve::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(140% 115% at 50% -28%, rgba(6,8,16,0) 58%, rgba(6,8,16,0.92) 59%, rgba(6,8,16,0.98) 72%, rgba(6,8,16,0) 73%);
  }
  .review-columns-curve::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 0;
    transform: translateX(-50%);
    width: 74%;
    height: 54px;
    border-radius: 0 0 999px 999px;
    background: linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02));
    box-shadow:
      0 1px 0 rgba(255,255,255,0.08) inset,
      0 18px 30px rgba(0,0,0,0.18);
    opacity: .5;
    filter: blur(.2px);
  }
  .review-col {
    overflow: hidden;
    position: relative;
  }
  .review-col-track {
    display: flex;
    flex-direction: column;
    gap: .9rem;
    animation: reviewVerticalMove 38s linear infinite;
  }
  .review-col-track.rev {
    animation-direction: reverse;
  }
  .review-group {
    display: flex;
    flex-direction: column;
    gap: .9rem;
  }
  .review-col:hover .review-col-track {
    animation-play-state: paused;
  }
  @keyframes reviewVerticalMove {
    0% { transform: translateY(0); }
    100% { transform: translateY(-50%); }
  }
  .review-card {
    position: relative;
    border-radius: 22px;
    border: 1px solid rgba(255,255,255,0.2);
    background:
      linear-gradient(180deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.12) 100%),
      linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 100%);
    color: #eef4ff;
    padding: 1.08rem 1.08rem 1rem;
    display: flex;
    flex-direction: column;
    gap: .82rem;
    min-height: 218px;
    box-shadow:
      0 18px 44px rgba(0,0,0,0.24),
      0 1px 0 rgba(255,255,255,0.34) inset,
      0 -1px 0 rgba(255,255,255,0.08) inset;
    overflow: hidden;
    transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
  }
  .review-card::before {
    content: "";
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: linear-gradient(90deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.26) 40%, rgba(255,255,255,0) 100%);
  }
  .review-card::after {
    content: "”";
    position: absolute;
    top: .55rem;
    right: .8rem;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 3rem;
    line-height: 1;
    color: rgba(255,255,255,0.12);
    pointer-events: none;
  }
  .review-card:hover {
    transform: translateY(-4px);
    border-color: rgba(255,255,255,0.34);
    box-shadow:
      0 22px 52px rgba(0,0,0,0.3),
      0 1px 0 rgba(255,255,255,0.42) inset,
      0 -1px 0 rgba(255,255,255,0.08) inset;
  }
  .review-stars {
    color: #ffd56a;
    letter-spacing: 1.9px;
    font-size: .76rem;
    text-shadow: 0 1px 10px rgba(255,213,106,0.2);
  }
  .review-text {
    font-size: .82rem;
    line-height: 1.72;
    color: rgba(242,246,255,0.9);
    flex: 1;
  }
  .review-author {
    display: flex;
    align-items: center;
    gap: .7rem;
    padding-top: .82rem;
    border-top: 1px solid rgba(255,255,255,0.12);
  }
  .review-avatar {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.16) 100%);
    color: #ffffff;
    font-size: .8rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-mono);
    box-shadow:
      inset 0 1px 0 rgba(255,255,255,0.38),
      0 8px 18px rgba(0,0,0,0.12);
    border: 1px solid rgba(255,255,255,0.14);
    overflow: hidden;
    flex-shrink: 0;
  }
  .review-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .review-author-name {
    font-weight: 700;
    font-size: .82rem;
    color: rgba(255,255,255,0.95);
  }
  .review-author-role {
    font-size: .65rem;
    color: rgba(226,234,249,0.64);
    font-family: var(--font-mono);
    letter-spacing: .2px;
  }

  .pricing-sec { background:var(--bg); }
  .price-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-top:3rem; }
  .price-card { background:var(--card); border:1px solid var(--border2); border-radius:14px; padding:1.8rem 1.4rem; transition:all .3s; }
  .price-card:hover { transform:translateY(-4px); box-shadow:0 12px 40px rgba(91,115,255,0.1); }
  .price-card.pop { border-color:rgba(91,115,255,0.4); background:linear-gradient(160deg,var(--card2),rgba(91,115,255,0.07)); }
  .pop-lbl { display:inline-block; background:var(--accent); color:#fff; font-size:.58rem; font-weight:700; padding:.2rem .65rem; border-radius:100px; letter-spacing:.5px; margin-bottom:.7rem; font-family:var(--font-mono); }
  .price-plan { font-family:var(--font-mono); font-size:.62rem; color:var(--accent); letter-spacing:2px; text-transform:uppercase; margin-bottom:.4rem; }
  .price-amt { font-family:var(--font-display); font-size:2.2rem; font-weight:700; line-height:1; }
  .price-amt sup { font-size:.9rem; font-weight:400; vertical-align:super; color:var(--muted); }
  .price-per { font-size:.78rem; color:var(--muted); }
  .price-feats { list-style:none; margin:1.2rem 0; }
  .price-feats li { padding:.4rem 0; font-size:.78rem; color:var(--muted); border-bottom:1px solid var(--border2); display:flex; gap:.5rem; align-items:flex-start; }
  .price-feats li:last-child { border:none; }
  .ck{color:var(--green);} .xk{color:var(--muted2);}
  .btn-p { width:100%; padding:.65rem; border-radius:7px; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .2s; font-family:var(--font-body); }
  .btn-pa { background:var(--accent); color:#fff; border:none; }
  .btn-pa:hover { box-shadow:0 0 20px var(--glow); }
  .btn-po { background:transparent; color:var(--accent); border:1px solid rgba(91,115,255,0.3); }
  .btn-po:hover { background:var(--accent-dim); }
  .trial-note { text-align:center; color:var(--muted); font-size:.72rem; font-family:var(--font-mono); margin-top:1.5rem; letter-spacing:.3px; }

  .panic-sec { background:linear-gradient(135deg,#050615 0%,#07050f 50%,#050615 100%); padding:5rem; border-top:1px solid var(--border); border-bottom:1px solid var(--border); position:relative; overflow:hidden; text-align:center; }
  .panic-sec::before { content:''; position:absolute; inset:0; background-image:linear-gradient(rgba(91,115,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(91,115,255,0.03) 1px,transparent 1px); background-size:40px 40px; }
  .panic-orb { position:absolute; top:50%; left:50%; width:600px; height:600px; transform:translate(-50%,-50%); background:radial-gradient(circle,rgba(91,115,255,0.08) 0%,transparent 65%); pointer-events:none; }
  .panic-content { position:relative; z-index:2; }
  .panic-sec h2 { font-family:var(--font-display); font-size:3rem; font-weight:700; letter-spacing:-2px; line-height:1.1; margin-bottom:1rem; }
  .panic-sec p { color:var(--muted); max-width:480px; margin:0 auto 2.5rem; line-height:1.8; font-size:.9rem; }
  .panic-chips { display:flex; gap:.6rem; justify-content:center; flex-wrap:wrap; margin-bottom:2.5rem; }
  .panic-chip { display:inline-flex; align-items:center; gap:.4rem; background:var(--card); border:1px solid var(--border2); padding:.4rem .9rem; border-radius:8px; font-size:.78rem; color:var(--muted); }

  .em-footer { background:#020308; border-top:1px solid var(--border2); padding:4rem 5rem 2rem; }
  .fg { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:3rem; }
  .fb .em-logo { font-size:1.1rem; margin-bottom:.8rem; display:block; }
  .fb p { color:var(--muted); font-size:.8rem; line-height:1.8; max-width:220px; }
  .fc h5 { font-family:var(--font-mono); font-size:.6rem; letter-spacing:3px; text-transform:uppercase; color:var(--accent); margin-bottom:1rem; }
  .fc ul { list-style:none; }
  .fc li { padding:.3rem 0; }
  .fc a { color:var(--muted); text-decoration:none; font-size:.8rem; transition:color .2s; }
  .fc a:hover { color:var(--ink); }
  .fb-bottom { margin-top:2.5rem; padding-top:1.5rem; border-top:1px solid var(--border2); display:flex; justify-content:space-between; align-items:center; }
  .fb-bottom p { color:var(--muted2); font-size:.72rem; font-family:var(--font-mono); }
  .soc-row { display:flex; gap:.5rem; }
  .soc { width:32px; height:32px; border:1px solid var(--border2); border-radius:7px; display:flex; align-items:center; justify-content:center; color:var(--muted); font-family:var(--font-mono); font-size:.65rem; text-decoration:none; transition:all .2s; }
  .soc:hover { border-color:var(--accent); color:var(--accent); }

  .em-fab { position:fixed; bottom:24px; left:24px; z-index:800; }
  .em-fab a { width:50px; height:50px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:1.2rem; box-shadow:0 4px 20px rgba(0,0,0,.4); cursor:pointer; color:#fff; transition:transform .2s; text-decoration:none; background:#25D366; }
  .em-fab a:hover { transform:scale(1.1); }

  @media(max-width:1100px) {
    .em-section,.panic-sec{padding-left:2.5rem;padding-right:2.5rem;}
    .hero-content-wrapper { padding: 9rem 2.5rem 2rem 2.5rem; flex-direction: column; gap: 3rem; }
    .hero-bottom-bar { padding: 0 2.5rem 3rem 2.5rem; }
    .hero-main-title { font-size: 4rem; }
    .hero-right { margin-top: 0; }
    .feature-cards-layout{grid-template-columns:1fr 1fr;}
    .feature-card-stack{grid-column:span 2;grid-template-columns:1fr 1fr;width:auto;}
    .pcard.tall,.pcard.short{width:100%;}
    .pcard.tall{height:420px;min-height:420px;}
    .pcard.short{height:238px;min-height:238px;}
    .community-sec{padding:3.75rem 2.25rem 4.25rem;}
    .community-shell,.community-copy{min-height:440px;}
    .community-copy{padding:2.6rem 1.6rem;}
    .crs-grid{grid-template-columns:1fr 1fr;} .sem-content{grid-template-columns:1fr 1fr;}
    .price-grid{grid-template-columns:1fr 1fr;} .c-spotlight{grid-template-columns:1fr;}
    .notes-layout{grid-template-columns:1fr;} .nl-left{position:static;}
    .mpq-layout{grid-template-columns:1fr;}
    .presence-title{font-size:clamp(1.7rem,4vw,2.6rem);} 
    .presence-bg{top:3.6rem;height:clamp(4rem,10vw,6.2rem);} 
    .presence-bg-track span{font-size:clamp(2.6rem,11vw,6rem);} 
    .presence-group{gap:.8rem;padding:0 .4rem;} 
    .presence-card{width:132px;}
    .review-columns{grid-template-columns:repeat(2,minmax(220px,1fr));max-height:560px;}
    .em-footer{padding-left:2.5rem;padding-right:2.5rem;} .fg{grid-template-columns:1fr 1fr;gap:2rem;}
  }
  @media(max-width:700px) {
    .em-desk-only { display: none !important; }
    .em-mob-only { display: block !important; }
    .em-nav-links,.em-nav-r .btn-ghost,.em-nav-r .btn-cta{display:none;}
    .hbg{display:flex;}
    .em-hero { height: 100svh; min-height: 750px; }
    .hero-bg-image { background-image: url('/assets/hero-mobile.png'); background-position: center bottom; }
    .hero-content-wrapper { padding: 6.5rem 1.5rem 0 1.5rem; text-align: left; flex-direction: column; gap: 0; justify-content: flex-start; }
    .hero-left { position: relative; height: 100%; max-width: 100%; align-items: flex-start; justify-content: flex-start; display: flex; flex-direction: column; }
    .hero-trust-badge { margin: 0 0 1.5rem 0; font-size: 0.65rem; padding: 0.4rem 0.8rem; background: rgba(4,6,15,0.8); border: 1px solid rgba(31,122,255,0.4); color: #82a8ff; }
    .hero-trust-badge svg { stroke: #1f7aff; width: 14px; height: 14px; }
    .hero-main-title { font-size: clamp(2.5rem, 12vw, 3.5rem); line-height: 1.05; letter-spacing: -1.5px; text-align: left; margin-bottom: 1rem; }
    .hero-main-title .mob-br { display: block; }
    .hero-main-title .text-grad { background: linear-gradient(110deg, #1f7aff 0%, #00e5c8 50%, #9a4dff 100%); background-size: 200% 200%; animation: heroGradientFlow 6s ease-in-out infinite; -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
    .hero-main-title .text-pink { background: linear-gradient(110deg, #ff4d94 0%, #9a4dff 50%, #ff4e6a 100%); background-size: 200% 200%; animation: heroGradientFlow 6s ease-in-out infinite reverse; -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
    .hero-description { width: 55%; font-size: 0.8rem; line-height: 1.5; color: #b0bbd2; text-align: left; margin: 14rem 0 0 0; position: relative; }
    .hero-right { display: none; }
    .hero-bottom-bar { position: absolute; inset: 0; padding: 0 1.2rem 1.2rem 1.2rem; display: flex; flex-direction: column; justify-content: flex-end; align-items: flex-start; pointer-events: none; }
    .hero-actions-row { position: absolute; bottom: 7rem; right: 1.2rem; width: 62%; flex-direction: column; gap: 0.8rem; pointer-events: auto; }
    .hero-actions-row button { width: 100%; justify-content: center; padding: 0.9rem 0.5rem; font-size: 0.85rem; border-radius: 8px; font-weight: 700; }
    .btn-outline-dark { background: transparent; border: 1px solid rgba(255,255,255,0.4); color: white; }
    .btn-solid-blue { background: #1f7aff; border: none; color: white; }
    .hero-stats-row { display: flex; flex-direction: row; justify-content: flex-start; width: auto; max-width: 100%; background: rgba(12, 14, 18, 0.75); border-radius: 12px; padding: 0.8rem 1rem; pointer-events: auto; margin-top: auto; border: none; gap: 1rem; }
    .hs-item { flex: none; align-items: center; text-align: center; gap: 0.3rem; padding-right: 1rem; border-right: 1px solid rgba(255,255,255,0.08); }
    .hs-item:last-child { padding-right: 0; border-right: none; }
    .hs-val { font-size: 1.1rem; gap: 0.3rem; justify-content: center; font-weight: 800; letter-spacing: -0.5px; }
    .hs-val svg { width: 14px; height: 14px; stroke-width: 2.5; stroke: #1f7aff; }
    .hs-lbl { font-size: 0.55rem; line-height: 1.3; color: #ffffff; font-weight: 500; }
    .em-section { padding: 2.5rem 1.2rem; }
    .feature-marquee{top:58%;height:3rem;}
    .feature-marquee-track span{font-size:2.3rem;}
    .feature-tagline{font-size:2.25rem; margin-top: 0;}
    .feature-showcase { overflow: visible; }
    .feature-showcase-bg { position: sticky; top: 0; height: 100vh; width: 100%; margin-bottom: -100vh; z-index: 0; }
    .feature-header-wrapper { position: sticky; top: 14vh; height: 86vh; margin-bottom: 0; pointer-events: none; z-index: 20; }
    .feature-header-wrapper > * { pointer-events: auto; }
    .feature-subline{font-size:.95rem;}
    .feature-cards-layout { display: block; width: 100%; margin-top: -46vh; padding-bottom: .75rem; position: relative; z-index: 5; }
    .pcard { position: sticky; width: 100%; border-radius: 20px; box-shadow: 0 -20px 40px rgba(0,0,0,0.7), 0 0 14px rgba(255,255,255,0.07); transition: transform 0.3s ease; }
    .feature-cards-layout > .pcard:nth-child(1) { top: 50vh; height: 47vh; margin-bottom: 0vh; z-index: 10; }
    .feature-cards-layout > .pcard:nth-child(2) { top: 53vh; height: 44vh; margin-bottom: 0vh; z-index: 11; }
    .feature-cards-layout > .pcard:nth-child(3) { top: 56vh; height: 41vh; margin-bottom: 0vh; z-index: 12; }
    .feature-cards-layout > .pcard:nth-child(4) { top: 59vh; height: 38vh; margin-bottom: 0vh; z-index: 13; }
    .community-sec{padding:2.75rem 1.25rem 3rem;}
    .community-shell,.community-copy{min-height:400px; border-radius:22px;}
    .community-copy{padding:2rem 1.1rem;}
    .community-lead{font-size:.88rem;}
    .community-count{font-size:clamp(2.5rem, 12vw, 4.1rem);}
    .community-stats {
      width: 100%;
      overflow: hidden;
      justify-content: center;
      flex-wrap: nowrap;
      margin-top: .7rem;
      padding-bottom: .15rem;
    }
    .community-stats-track {
      position: relative;
      width: 100%;
      min-height: 88px;
      display: block;
    }
    .community-stat{
      position: absolute;
      inset: 0;
      min-width: 100%;
      max-width: 100%;
      padding:.72rem .9rem;
      text-align: center;
      opacity: 0;
      transform: translateX(10%);
      animation: communityStatSingleMobile 9s ease-in-out infinite;
      will-change: opacity, transform;
    }
    .community-stat strong,
    .community-stat span {
      text-align: center;
    }
    .community-stats-track .community-stat:nth-child(1) { animation-delay: 0s; }
    .community-stats-track .community-stat:nth-child(2) { animation-delay: 3s; }
    .community-stats-track .community-stat:nth-child(3) { animation-delay: 6s; }
    .presence-line{padding:0 .8rem;margin-bottom:.8rem;}
    .presence-pill{font-size:.56rem;letter-spacing:1.2px;padding:.32rem .65rem;}
    .presence-title{font-size:1.58rem;line-height:1.2;}
    .presence-bg{top:3.2rem;height:3.9rem;}
    .presence-bg-track span{font-size:3.1rem;}
    .presence-sub{font-size:.86rem;}
    .presence-rows{margin-top:1.5rem;gap:.75rem;}
    .presence-group{gap:.62rem;padding:0 .31rem;}
    .presence-card{width:112px;padding:.5rem .42rem .58rem;}
    .presence-name{font-size:.56rem;}
    .pcard.tall,.pcard.short{height:310px;min-height:310px;}
    .crs-grid{grid-template-columns:1fr;}
    .sem-content{grid-template-columns:1fr;} .price-grid{grid-template-columns:1fr;}
    .panic-sec h2{font-size:2.1rem;} .stat-strip{flex-wrap:wrap;width:90%;}
    .fg{grid-template-columns:1fr;} .fb-bottom{flex-direction:column;gap:.8rem;text-align:center;}
    .em-section,.panic-sec{padding-left:1.5rem;padding-right:1.5rem;}
    .em-footer{padding:3rem 1.5rem 1.5rem;} .em-nav{padding:1rem 1.5rem;}
    .review-shell{padding:0 .9rem;}
    .review-title{font-size:1.72rem;}
    .review-ratings{gap:.55rem;}
    .review-rate-card{min-width:148px;padding:.55rem .7rem;}
    .review-columns{grid-template-columns:1fr;max-height:500px;}
    .review-card{min-height:190px;}
  }
  .team-sec { background: var(--bg2); position: relative; overflow: hidden; padding: 0; }
  .team-grid { 
    display: grid; 
    grid-template-columns: repeat(3, 1fr); 
    gap: 2rem; 
    max-width: 100%; 
    margin: 0 auto; 
    position: relative; 
    z-index: 2; 
    height: 760px;
  }
  .team-backdrop-copy {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem;
  }
  .team-backdrop-inner {
    width: min(920px, 92vw);
    opacity: 0.22;
    transform: translateY(-262px);
  }
  .team-backdrop-kicker {
    font-family: var(--font-mono);
    font-size: .72rem;
    letter-spacing: 2.8px;
    text-transform: uppercase;
    color: rgba(0, 229, 200, 0.82);
    margin-bottom: .8rem;
  }
  .team-backdrop-title {
    font-family: var(--font-display);
    font-size: clamp(2.3rem, 5.6vw, 5.6rem);
    line-height: .95;
    letter-spacing: -1.8px;
    font-weight: 700;
    color: rgba(232, 238, 255, 0.9);
    margin-bottom: .75rem;
  }
  .team-backdrop-sub {
    font-family: var(--font-body);
    font-size: .9rem;
    line-height: 1.65;
    color: rgba(232, 238, 255, 0.78);
  }
  .team-beams-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    opacity: 0.9;
  }
  
  .team-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    pointer-events: none;
  }
  .team-item > * { pointer-events: auto; }
  .team-item.founder-shift .id-info {
    transform: translateX(-100px);
  }
  .team-item.lead-shift .id-info {
    transform: translateX(100px);
  }
  .team-label-row {
    position: absolute;
    left: 50%;
    bottom: 2.2rem;
    transform: translateX(-50%);
    width: min(860px, 92vw);
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: .5rem;
    z-index: 3;
    pointer-events: none;
  }

  .team-canvas-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: auto;
  }
  .team-lanyard-box:active { cursor: grabbing; }
  .id-info { text-align: center; position: relative; z-index: 10; pointer-events: none; }
  .id-name {
    font-family: var(--font-display);
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 0.3rem;
  }
  .id-role {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--accent);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    display: block;
    margin-bottom: 1rem;
  }
  .id-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding-top: 1rem;
    border-top: 1px dashed rgba(255, 255, 255, 0.1);
    margin-top: auto;
  }
  .id-auth {
    font-family: var(--font-mono);
    font-size: 0.55rem;
    color: var(--muted);
    line-height: 1.2;
    text-align: left;
  }
  .id-qr {
    width: 50px;
    height: 50px;
    background: #fff;
    padding: 4px;
    border-radius: 6px;
    opacity: 0.8;
  }
  .id-qr-box {
    width: 100%;
    height: 100%;
    background: repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) 50% / 8px 8px;
  }

  @media(max-width: 900px) {
    .team-grid { grid-template-columns: 1fr; max-width: 380px; height: 620px; }
    .team-backdrop-inner { transform: translateY(-220px); }
    .team-item.founder-shift .id-info { transform: translateX(-88px); }
    .team-item.lead-shift .id-info { transform: translateX(34px); }
    .team-label-row {
      width: min(620px, 95vw);
      bottom: 1.5rem;
    }
    .id-name { font-size: 1.1rem; }
    .id-role { font-size: .66rem; }
  }
`;


const testimonials1 = [
  { emoji: "😅", avatar: "/assets/reviews/arjun.jpg", text: '"Failed my first C lab test. Found ENGImitra, went through the C Master Course, and cleared the backlog in 3 weeks. Wish I found this Day 1."', name: "Arjun M.", role: "CSE · 1st Year · MAKAUT" },
  { emoji: "🎯", avatar: "/assets/reviews/priyanka.jpg", text: '"The MPQs for Engineering Maths were insane — 70% of questions in my ESE came from the ENGImitra bank. Scored 82 out of 100."', name: "Priyanka S.", role: "ECE · Sem 2 · VTU" },
  { emoji: "🔧", avatar: "/assets/reviews/rohan.jpg", text: '"As a Mechanical student who never coded before, the C course on ENGImitra was the only thing that got me through the programming paper."', name: "Rohan K.", role: "ME · 1st Year · AKTU" },
  { emoji: "📈", avatar: "/assets/reviews/sneha.jpg", text: '"The PDF notes are so clean. No fluff, no 800-page textbook confusion. My Applied Physics score went from 45 to 78 in one semester."', name: "Sneha R.", role: "Civil · 1st Year · SPPU" },
  { emoji: "🌙", avatar: "/assets/reviews/deepak.jpg", text: '"Mentor support at 1am before my viva? They answered in 4 minutes. That\'s the reason I passed. No joke."', name: "Deepak N.", role: "EE · Sem 1 · GTU" },
];
const testimonials2 = [
  { emoji: "💪", avatar: "/assets/reviews/vaibhav.jpg", text: '"I was about to drop out after Sem 1. ENGImitra\'s structured approach completely changed how I study. Now I\'m in the top 20% of my batch."', name: "Vaibhav T.", role: "IT · 2nd Year · RGPV" },
  { emoji: "⚗️", avatar: "/assets/reviews/fatima.jpg", text: '"My college notes were useless. ENGImitra\'s Engineering Chemistry notes actually explain WHY — not just what. Made all the difference."', name: "Fatima A.", role: "Chemical · 1st Year · MU" },
  { emoji: "🗺️", avatar: "/assets/reviews/nikhil.jpg", text: '"The semester roadmap feature is genius. Knew what to focus on 3 weeks before exams and didn\'t panic at all. First time ever."', name: "Nikhil P.", role: "ECE · Sem 2 · AKTU" },
  { emoji: "💰", avatar: "/assets/reviews/ritika.jpg", text: '"₹499 for the starter plan and I\'m scoring better than friends who paid ₹80,000 for coaching. Value is unmatched."', name: "Ritika B.", role: "CSE · 1st Year · MAKAUT" },
];

const reviewCards = [...testimonials1, ...testimonials2];
const reviewColumns = [
  reviewCards.slice(0, 4),
  reviewCards.slice(2, 6),
  reviewCards.slice(4, 8),
  [...reviewCards.slice(1, 4), ...reviewCards.slice(6, 8)]
];

const presenceColleges = [
  { name: "IIT Hyderabad", logo: "/assets/colleges/iit-hyderabad.webp" },
  { name: "VIT Vellore", logo: "/assets/colleges/vit-vellore.webp" },
  { name: "NIT Silchar", logo: "/assets/colleges/nit-silchar.webp" },
  { name: "NIT Patna", logo: "/assets/colleges/nit-patna.webp" },
  { name: "Christ University", logo: "/assets/colleges/christ-university.webp" },
  { name: "KIIT", logo: "/assets/colleges/kiit.webp" },
  { name: "MAKAUT", logo: "/assets/colleges/makaut.webp" },
  { name: "Institute of Engineering and Management", logo: "/assets/colleges/iem.jpg" },
  { name: "BITS Pilani", logo: "/assets/colleges/bits-pilani.webp" },
  { name: "NIT Durgapur", logo: "/assets/colleges/nit-durgapur.webp" },
  { name: "Jadavpur University", logo: "/assets/colleges/jadavpur-university.webp" },
  { name: "University of Engineering and Management", logo: "/assets/colleges/uem.jpg" }
];

const presenceSplitIndex = Math.ceil(presenceColleges.length / 2);
const presenceRowOne = presenceColleges.slice(0, presenceSplitIndex);
const presenceRowTwo = presenceColleges.slice(presenceSplitIndex);

const SPLINE_SCENE_URL = "https://prod.spline.design/gPO1Km2kV8Vlzp5r/scene.splinecode";

const preloadImage = (src) => new Promise((resolve) => {
  const image = new Image();
  image.decoding = "async";
  image.onload = resolve;
  image.onerror = resolve;
  image.src = src;
});

const preloadFetch = (src, options = {}) => (
  fetch(src, { cache: "force-cache", ...options }).catch(() => null)
);

const addPreconnect = (href) => {
  if (typeof document === "undefined") return;
  if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preconnect";
  link.href = href;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
};

const preloadAssets = async (onProgress) => {
  const imageAssets = [
    "/assets/hmsc1.webp",
    "/assets/hmsc2.webp",
    "/assets/hmsc3.webp",
    "/assets/signal.png",
    "/assets/verified.png",
    "/assets/google.png",
    "/assets/linkedin.png",
    "/assets/wingoodies.png",
    "/assets/bronze.png",
    "/assets/silver.png",
    "/assets/gold.png",
    "/assets/team/1.png",
    "/assets/team/2.png",
    "/assets/team/3.png",
    "/assets/team/4.png",
    ...reviewCards.map((item) => item.avatar).filter(Boolean),
    ...presenceColleges.map((college) => college.logo)
  ];

  const fetchAssets = [
    "/assets/lanyard/card.glb",
    "/assets/lanyard/lanyard.png",
    SPLINE_SCENE_URL
  ];

  addPreconnect("https://prod.spline.design");

  const tasks = [
    ...[...new Set(imageAssets)].map((src) => () => preloadImage(src)),
    ...[...new Set(fetchAssets)].map((src) => () => (
      src.startsWith("http")
        ? preloadFetch(src, { mode: "no-cors" })
        : preloadFetch(src)
    ))
  ];

  let completed = 0;
  onProgress(8);

  await Promise.all(tasks.map((task) => (
    task().finally(() => {
      completed += 1;
      onProgress(Math.min(100, Math.round((completed / tasks.length) * 100)));
    })
  )));
};

/* GridMotion and its items removed (unused) to reduce bundle warnings and runtime overhead. */

const ambassadorStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400&display=swap');
  .amb-section,
  .amb-section * {
    box-sizing: border-box;
  }

  .amb-section {
    background: linear-gradient(135deg, #070910 0%, rgba(91,115,255,0.06) 100%);
    padding: 4rem 2rem 5rem;
    position: relative;
    border-top: 1px solid rgba(91,115,255,0.12);
    border-bottom: 1px solid rgba(91,115,255,0.12);
    font-family: 'DM Sans', sans-serif;
  }

  .amb-ruled-top {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent 0%, rgba(232,238,255,0.3) 20%, rgba(232,238,255,0.3) 80%, transparent 100%);
  }

  .amb-inner {
    max-width: 1160px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1.1fr;
    gap: 0 3rem;
    align-items: start;
    position: relative;
    z-index: 2;
  }

  .amb-left {
    grid-column: 1;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .amb-eyebrow {
    font-family: 'DM Mono', monospace;
    font-size: .68rem;
    letter-spacing: .18em;
    text-transform: uppercase;
    color: #ffb830;
    margin: 0 0 .8rem;
  }

  .amb-headline {
    font-family: 'Playfair Display', serif;
    font-size: clamp(2.4rem, 4.5vw, 3.8rem);
    font-weight: 700;
    color: #e8eeff;
    line-height: 1.08;
    margin: 0;
    text-wrap: balance;
  }

  .amb-headline em {
    font-style: italic;
    font-weight: 400;
    color: #00e5c8;
  }

  .amb-join-btn {
    display: inline-flex;
    align-items: center;
    gap: .5rem;
    background: #5b73ff;
    color: #faf7f2;
    text-decoration: none;
    font-size: .82rem;
    font-weight: 500;
    letter-spacing: .04em;
    padding: .85rem 1.6rem;
    border-radius: 6px;
    width: fit-content;
    box-shadow: 0 4px 12px rgba(91,115,255,0.3);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .amb-join-btn:hover {
    background: linear-gradient(90deg, #6b7bff 0%, #4be2c0 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(91,115,255,0.36);
  }

  .amb-avatar-row {
    display: flex;
    align-items: center;
    gap: .75rem;
  }

  .amb-avatars {
    display: flex;
  }

  .amb-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid #070910;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: .68rem;
    font-weight: 500;
    margin-left: -8px;
    color: #fff;
    flex-shrink: 0;
  }

  .amb-avatar:first-child {
    margin-left: 0;
  }

  .av1 { background: #00e5c8; color: #04050a; }
  .av2 { background: #5b73ff; }
  .av3 { background: #ffb830; color: #04050a; }
  .av4 { background: #ff4e6a; }
  .av5 { background: #00d68f; color: #04050a; }

  .amb-avatar-label {
    font-size: .82rem;
    font-weight: 500;
    color: #e8eeff;
    margin: 0;
  }

  .amb-avatar-sub {
    font-size: .72rem;
    color: #5a6380;
    margin: 1px 0 0;
  }

  .amb-steps-section {
    margin-top: 1rem;
  }

  .amb-col-label {
    font-family: 'DM Mono', monospace;
    font-size: .62rem;
    letter-spacing: .2em;
    text-transform: uppercase;
    color: #5a6380;
    margin: 0 0 1.2rem;
  }

  .amb-step {
    display: grid;
    grid-template-columns: 2.2rem 1fr;
    gap: 0 1.2rem;
    margin-bottom: 1.8rem;
    position: relative;
  }

  .amb-step:last-child {
    margin-bottom: 0;
  }

  .amb-step-num {
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    border: 1.5px solid #5b73ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Mono', monospace;
    font-size: .68rem;
    color: #5b73ff;
    flex-shrink: 0;
    position: relative;
    background: rgba(91,115,255,0.1);
    z-index: 1;
    transition: all .18s ease;
    cursor: pointer;
    will-change: transform, box-shadow, background;
  }

  .amb-steps-container {
    display: block;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .amb-steps-container::-webkit-scrollbar {
    display: none;
  }

  /* background dotgrid container (behind content) */
  .amb-bg-dotgrid {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .amb-tier-logo {
    width: 34px;
    height: 34px;
    object-fit: contain;
    display: block;
  }

  .amb-step:not(:last-child) .amb-step-num::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 2.2rem;
    bottom: -1.8rem;
    width: 1px;
    background: rgba(91,115,255,0.15);
    transform: translateX(-50%);
  }

  .amb-step-num:hover,
  .amb-step-num:focus {
    background: linear-gradient(90deg, #6b7bff 0%, #4be2c0 100%);
    color: #fff;
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 22px rgba(91,115,255,0.36);
    border-color: rgba(255,255,255,0.12);
    outline: none;
  }

  .amb-step-body {
    padding-top: .1rem;
  }

  .amb-step-title {
    font-weight: 500;
    font-size: .9rem;
    color: #e8eeff;
    margin: 0 0 .3rem;
  }

  .amb-step-desc {
    font-size: .8rem;
    color: #5a6380;
    line-height: 1.65;
    margin: 0;
  }

  .amb-right {
    grid-column: 2;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .amb-tier-card {
    border: 1px solid rgba(91,115,255,0.2);
    border-radius: 12px;
    padding: 1.1rem 1.3rem;
    background: rgba(13,16,32,0.7);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    gap: 1.1rem;
    margin-bottom: .75rem;
    transition: border-color 0.3s;
  }

  .amb-tier-card:last-child {
    margin-bottom: 0;
  }

  .amb-tier-dot {
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Playfair Display', serif;
    font-size: .72rem;
    font-weight: 700;
  }

  .amb-tier-dot.bronze { background: rgba(255,184,48,.15); color: #ffb830; border: 1.5px solid rgba(255,184,48,.35); }
  .amb-tier-dot.silver { background: rgba(0,229,200,.15); color: #00e5c8; border: 1.5px solid rgba(0,229,200,.35); }
  .amb-tier-dot.gold { background: rgba(91,115,255,.15); color: #5b73ff; border: 1.5px solid rgba(91,115,255,.4); }

  .amb-tier-info { flex: 1; }

  .amb-tier-name {
    font-weight: 500;
    font-size: .88rem;
    color: #e8eeff;
    margin: 0 0 .18rem;
  }

  .amb-tier-desc {
    font-size: .76rem;
    color: #5a6380;
    line-height: 1.5;
    margin: 0;
  }

  .amb-tier-badge {
    font-family: 'DM Mono', monospace;
    font-size: .58rem;
    letter-spacing: .12em;
    text-transform: uppercase;
    padding: .28rem .6rem;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .amb-tier-badge.bronze { color: #ffb830; background: rgba(255,184,48,.12); }
  .amb-tier-badge.silver { color: #00e5c8; background: rgba(0,229,200,.12); }
  .amb-tier-badge.gold { color: #5b73ff; background: rgba(91,115,255,.12); }

  .amb-goodies-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.45rem;
    font-weight: 700;
    color: #e8eeff;
    margin: 0 0 .75rem;
  }

  .amb-swag-block {
    width: 100%;
    background: rgba(13,16,32,0.82);
    border: 1px solid rgba(91,115,255,0.18);
    border-radius: 14px;
    overflow: hidden;
  }

  .amb-swag-block img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }

  .amb-pull-quote {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    padding-top: .5rem;
  }

  .amb-blockquote {
    font-family: 'Playfair Display', serif;
    font-style: italic;
    font-size: 1.2rem;
    line-height: 1.55;
    color: rgba(232,238,255,0.92);
    flex: 1;
    padding-left: 1.5rem;
    border-left: 4px solid #5b73ff;
    border-radius: 0;
    margin: 0;
  }

  .amb-attribution {
    flex-shrink: 0;
    min-width: 170px;
  }

  .amb-attribution-name {
    font-weight: 500;
    font-size: .85rem;
    color: #e8eeff;
    margin: 0;
  }

  .amb-attribution-role {
    font-size: .75rem;
    color: #5a6380;
    margin: .35rem 0 0;
    line-height: 1.4;
  }

  .amb-perks-strip {
    grid-column: 1 / -1;
    margin-top: 2.5rem;
    border-top: 1px solid rgba(91,115,255,0.12);
    border-bottom: 1px solid rgba(91,115,255,0.12);
    padding: 1.5rem 0;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }

  .amb-perk {
    padding: 0 1.5rem;
    border-left: 1px solid rgba(91,115,255,0.12);
  }

  .amb-perk:first-child {
    border-left: none;
    padding-left: 0;
  }

  .amb-perk-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 1rem;
    opacity: .8;
    filter: brightness(0) saturate(100%) invert(85%) sepia(8%) saturate(200%) hue-rotate(180deg);
  }

  .amb-perk-label {
    font-family: 'DM Mono', monospace;
    font-size: .62rem;
    letter-spacing: .18em;
    text-transform: uppercase;
    color: #00e5c8;
    margin: 0 0 .5rem;
  }

  .amb-perk-title {
    font-weight: 600;
    font-size: .9rem;
    color: #e8eeff;
    margin: 0 0 .3rem;
  }

  .amb-perk-desc {
    font-size: .78rem;
    color: #5a6380;
    line-height: 1.65;
    margin: 0;
  }

  @media (max-width: 768px) {
    .amb-section { padding: 4.5rem 0 0 0; }
    .amb-inner { grid-template-columns: 1fr; gap: 2.5rem 0; padding: 0 1.5rem; }
    .amb-left, .amb-right { grid-column: 1; grid-row: auto; }
    
    .amb-right > div:nth-child(1) { order: 3; margin-top: 2rem; }
    .amb-right > div:nth-child(2) { order: 1; }
    .amb-right > div:nth-child(3) { order: 2; }

    .amb-perks-strip { grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 0; border-top: none; padding-top: 0; }
    .amb-perk { border-left: none; padding: 0; }
    .amb-pull-quote { flex-direction: column; gap: 1.5rem; }
    
    .amb-join-btn {
      animation: ambPulse 3s infinite ease-in-out;
      margin-bottom: 0.5rem;
    }

    @keyframes ambPulse {
      0%, 100% { transform: scale(1); box-shadow: 0 4px 12px rgba(91,115,255,0.3); }
      50% { transform: scale(1.04); box-shadow: 0 8px 24px rgba(91,115,255,0.5); }
    }

    .amb-steps-container {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto auto;
      overflow: hidden;
      gap: 1.8rem 0;
      padding: 0.5rem 0;
    }
    .amb-step {
      grid-column: 1;
      margin-bottom: 0 !important;
    }
    .amb-step:nth-child(1), .amb-step:nth-child(3) {
      grid-row: 1;
    }
    .amb-step:nth-child(2), .amb-step:nth-child(4) {
      grid-row: 2;
    }
    
    .amb-step:nth-child(1) { animation: step1Anim 8s infinite cubic-bezier(0.65, 0, 0.35, 1); }
    .amb-step:nth-child(3) { animation: step3Anim 8s infinite cubic-bezier(0.65, 0, 0.35, 1); }
    
    .amb-step:nth-child(2) { animation: step2Anim 8s infinite cubic-bezier(0.65, 0, 0.35, 1); }
    .amb-step:nth-child(4) { animation: step4Anim 8s infinite cubic-bezier(0.65, 0, 0.35, 1); }

    .amb-tiers-container {
      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      overflow: hidden;
      padding: 0.5rem 0;
    }
    .amb-tier-card {
      grid-column: 1;
      grid-row: 1;
      margin-bottom: 0 !important;
    }
    .amb-tier-card:nth-child(1) { animation: tierBronzeAnim 12s infinite cubic-bezier(0.65, 0, 0.35, 1); }
    .amb-tier-card:nth-child(2) { animation: tierSilverAnim 12s infinite cubic-bezier(0.65, 0, 0.35, 1); }
    .amb-tier-card:nth-child(3) { animation: tierGoldAnim 12s infinite cubic-bezier(0.65, 0, 0.35, 1); }

    @keyframes tierBronzeAnim {
      0%, 25% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      33%, 58% { transform: translateX(110%); opacity: 0; pointer-events: none; }
      58.1%, 91% { transform: translateX(-110%); opacity: 0; pointer-events: none; }
      99%, 100% { transform: translateX(0); opacity: 1; pointer-events: auto; }
    }
    @keyframes tierSilverAnim {
      0%, 25% { transform: translateX(-110%); opacity: 0; pointer-events: none; }
      33%, 58% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      66%, 100% { transform: translateX(-110%); opacity: 0; pointer-events: none; }
    }
    @keyframes tierGoldAnim {
      0%, 58% { transform: translateX(110%); opacity: 0; pointer-events: none; }
      66%, 91% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      99%, 100% { transform: translateX(110%); opacity: 0; pointer-events: none; }
    }

    @keyframes step1Anim {
      0%, 35% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      45%, 85% { transform: translateX(110%); opacity: 0; pointer-events: none; }
      95%, 100% { transform: translateX(0); opacity: 1; pointer-events: auto; }
    }
    @keyframes step3Anim {
      0%, 35% { transform: translateX(-110%); opacity: 0; pointer-events: none; }
      45%, 85% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      95%, 100% { transform: translateX(-110%); opacity: 0; pointer-events: none; }
    }

    @keyframes step2Anim {
      0%, 45% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      55%, 85% { transform: translateX(-110%); opacity: 0; pointer-events: none; }
      95%, 100% { transform: translateX(0); opacity: 1; pointer-events: auto; }
    }
    @keyframes step4Anim {
      0%, 45% { transform: translateX(110%); opacity: 0; pointer-events: none; }
      55%, 85% { transform: translateX(0); opacity: 1; pointer-events: auto; }
      95%, 100% { transform: translateX(110%); opacity: 0; pointer-events: none; }
    }
    
    .amb-step:nth-child(2) .amb-step-num::after {
      display: none;
    }
  }
`;

const ambassadorSwagHero = '/assets/wingoodies.png';

/* removed unused ambassadorGoodieOrbs to satisfy CI lint rules */

const ambassadorSteps = [
  {
    num: "01",
    title: "Apply through the ambassador channel",
    desc: "Fill out a quick form in our WhatsApp community. Tell us about your college and why you want to represent ENGImitra.",
  },
  {
    num: "02",
    title: "Get verified and receive your toolkit",
    desc: "We review your profile, onboard you, and hand you everything you need — creatives, links, and your referral tracker.",
  },
  {
    num: "03",
    title: "Start promoting and earn rewards",
    desc: "Run campaigns on campus, track referrals in real time, and unlock tier upgrades as your impact grows.",
  },
  {
    num: "04",
    title: "Grow into a leader",
    desc: "Top ambassadors gain certificates, exclusive access, and priority team support — plus a verified leadership badge.",
  },
];

const ambassadorTiers = [
  {
    key: "bronze",
    symbol: "Br",
    name: "Bronze",
    desc: "Start representing your college. Build your first cohort of referred users and earn your place in the network.",
    badge: "Entry",
  },
  {
    key: "silver",
    symbol: "Si",
    name: "Silver",
    desc: "Unlock growth perks, referral milestones, and access to exclusive community events and product previews.",
    badge: "Growth",
  },
  {
    key: "gold",
    symbol: "Au",
    name: "Gold",
    desc: "Our top ambassadors. Leadership recognition, co-created campaigns, and priority access to the ENGImitra team.",
    badge: "Elite",
  },
];

const ambassadorPerks = [
  {
    label: "Recognition",
    title: "Verified campus role",
    desc: "A badge that signals leadership and real-world experience to peers and recruiters.",
    icon: (
      <svg className="amb-perk-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="10" r="6" stroke="#e8eeff" strokeWidth="1.5" />
        <path d="M4 28c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#e8eeff" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: "Tracking",
    title: "Referral dashboard",
    desc: "See every referral, milestone, and reward in real time - no guesswork.",
    icon: (
      <svg className="amb-perk-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="6" width="24" height="20" rx="2" stroke="#e8eeff" strokeWidth="1.5" />
        <path d="M4 12h24" stroke="#e8eeff" strokeWidth="1.5" />
        <circle cx="10" cy="19" r="2" fill="#e8eeff" />
        <path d="M15 19h8" stroke="#e8eeff" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    label: "Rewards",
    title: "Milestone perks",
    desc: "Unlock tangible rewards, certificates, and exclusive access as you hit each tier.",
    icon: (
      <svg className="amb-perk-icon" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <polygon points="16,4 20,12 28,13 22,19 24,27 16,23 8,27 10,19 4,13 12,12" stroke="#e8eeff" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    label: "Support",
    title: "Priority team access",
    desc: "Direct line to the ENGImitra team for creatives, strategy, and campaign guidance.",
    icon: (
      <svg className="amb-perk-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 12v1a8 8 0 0016 0v-1" stroke="#e8eeff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 12a6 6 0 0112 0v2a3 3 0 01-3 3h-1v-4H8v4H7a3 3 0 01-3-3v-2z" stroke="#e8eeff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 18v2a1 1 0 001 1h1v-3H8z" stroke="#e8eeff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

const ambassadorAvatars = [
  { cls: "av1", initials: "KS" },
  { cls: "av2", initials: "PR" },
  { cls: "av3", initials: "AM" },
  { cls: "av4", initials: "SN" },
  { cls: "av5", initials: "RD" },
];

function BrandAmbassadorSection() {
  return (
    <>
      <style>{ambassadorStyles}</style>
      <section className="amb-section" id="brand-ambassador">
        <div className="amb-bg-dotgrid">
          {!IS_BOT && (
            <DotGrid
              mode="grid"
              dotSize={8}
              gap={120}
              baseColor="#0b0d14"
              activeColor="#5b73ff"
              proximity={140}
              shockRadius={200}
              shockStrength={3}
              resistance={650}
              returnDuration={1.2}
            />
          )}
        </div>
        <div className="amb-ruled-top" />
        <div className="amb-inner">
          <div className="amb-left">
            <div>
              <div className="amb-eyebrow">Ambassador Program</div>
              <h2 className="amb-headline">
                Build real influence<br />on your <em>campus.</em>
              </h2>
            </div>

            <a
              className="amb-join-btn"
              href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z"
              target="_blank"
              rel="noreferrer"
            >
              Join the network →
            </a>

            <div className="amb-avatar-row">
              <div className="amb-avatars">
                {ambassadorAvatars.map((av) => (
                  <div key={av.initials} className={`amb-avatar ${av.cls}`}>
                    {av.initials}
                  </div>
                ))}
              </div>
              <div>
                <div className="amb-avatar-label">Gold Ambassadors</div>
                <div className="amb-avatar-sub">Joined by 120+ ambassadors across India</div>
              </div>
            </div>

            <div className="amb-steps-section">
              <div className="amb-col-label">How it works</div>
              <div className="amb-steps-container">
                {ambassadorSteps.map((step) => (
                  <div className="amb-step" key={step.num}>
                    <div className="amb-step-num">{step.num}</div>
                    <div className="amb-step-body">
                      <div className="amb-step-title">{step.title}</div>
                      <div className="amb-step-desc">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="amb-right">
            <div>
              <div className="amb-col-label">Ambassador tiers</div>
              <div className="amb-tiers-container">
                {ambassadorTiers.map((tier) => (
                  <div className="amb-tier-card" key={tier.key}>
                    <div className={`amb-tier-dot ${tier.key}`}>
                      <img src={`/assets/${tier.key}.png`} alt={`${tier.name} logo`} className="amb-tier-logo" />
                    </div>
                    <div className="amb-tier-info">
                      <div className="amb-tier-name">{tier.name}</div>
                      <div className="amb-tier-desc">{tier.desc}</div>
                    </div>
                    <div className={`amb-tier-badge ${tier.key}`}>{tier.badge}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="amb-goodies-title">Win Swags and Goodies</h3>
              <div className="amb-swag-block">
                <img src={ambassadorSwagHero} alt="Win swags and goodies - t-shirt, hoodie, bottle, watch, mug, earbuds and more" />
              </div>
            </div>

            <div className="amb-pull-quote">
              <blockquote className="amb-blockquote">
                "Being an ambassador gave me more real-world experience than any internship I applied for."
              </blockquote>
              <div className="amb-attribution">
                <div className="amb-attribution-name">Sarah N., Founder of Tech Club</div>
                <div className="amb-attribution-role">Chandigarh Institute of Engineering & Technology</div>
              </div>
            </div>
          </div>

          <div className="amb-perks-strip">
            {ambassadorPerks.map((perk) => (
              <div className="amb-perk" key={perk.label}>
                {perk.icon}
                <div className="amb-perk-label">{perk.label}</div>
                <div className="amb-perk-title">{perk.title}</div>
                <div className="amb-perk-desc">{perk.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function ENGImitra() {
  const [isPreloading, setIsPreloading] = useState(!IS_BOT);
  const [lanyardState, setLanyardState] = useState({ currentIndex: 0, pullUp: false, isMobile: false });

  useEffect(() => {
    const handleLanyardUpdate = (e) => setLanyardState(e.detail);
    window.addEventListener('lanyard-update', handleLanyardUpdate);
    return () => window.removeEventListener('lanyard-update', handleLanyardUpdate);
  }, []);

  // Refs for animation to prevent React re-renders
  const progressTopRef = useRef(null);
  const progressWaveRef = useRef(null);
  const progressTextRef = useRef(null);

  const studentsRef = useRef(null);
  const passRateRef = useRef(null);
  const hoursRef = useRef(null);
  const trialRef = useRef(null);

  const communityCountRef = useRef(null);

  const glowRef = useRef(null);
  const statStripRef = useRef(null);
  const statsHasAnimatedRef = useRef(false);
  const communitySectionRef = useRef(null);
  const communityCountHasAnimatedRef = useRef(false);

  useEffect(() => {
    if (IS_BOT) return;

    const lenis = new Lenis({
      lerp: 0.08, // Increased from 0.021 for more responsive yet smooth momentum
      wheelMultiplier: 1,
      smoothWheel: true,
      normalizeWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (IS_BOT) return;
    const handleMouseMove = (e) => {
      if (glowRef.current) {
        const x = e.clientX - 200;
        const y = e.clientY - 200;
        glowRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
    };
    document.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    if (IS_BOT) {
      document.body.classList.add('is-bot');
      return;
    }

    let active = true;

    const introDuration = 8800;

    const maximumTimer = setTimeout(() => {
      if (!active) return;
      if (progressTextRef.current) progressTextRef.current.innerText = `loading... 100 %`;
      setIsPreloading(false);
    }, 23000);

    const animateWaveProgress = () => new Promise((resolve) => {
      if (progressTopRef.current) progressTopRef.current.classList.add('active');
      if (progressWaveRef.current) progressWaveRef.current.classList.add('active');

      let val = 0;
      const interval = setInterval(() => {
        if (!active) {
          clearInterval(interval);
          return resolve();
        }
        val += 2;
        if (val > 100) val = 100;
        if (progressTextRef.current) progressTextRef.current.innerText = `loading... ${val} %`;
        if (val === 100) {
          clearInterval(interval);
          resolve();
        }
      }, 9500 / 50);
    });

    const startPreloading = () => {
      if (!active) return;
      Promise.all([
        preloadAssets(() => { }),
        animateWaveProgress()
      ]).then(() => {
        if (!active) return;
        clearTimeout(maximumTimer);
        if (progressTextRef.current) progressTextRef.current.innerText = `loading... 100 %`;
        window.setTimeout(() => {
          if (active) setIsPreloading(false);
        }, 700);
      });
    };

    const introTimeout = setTimeout(startPreloading, introDuration);

    return () => {
      active = false;
      clearTimeout(maximumTimer);
      clearTimeout(introTimeout);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = isPreloading ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isPreloading]);

  useEffect(() => {
    if (!statStripRef.current || statsHasAnimatedRef.current) return;

    const targets = {
      students: 50,
      passRate: 95,
      hours: 1000,
      trial: 7
    };

    if (IS_BOT) {
      if (studentsRef.current) studentsRef.current.innerText = targets.students;
      if (passRateRef.current) passRateRef.current.innerText = targets.passRate;
      if (hoursRef.current) hoursRef.current.innerText = targets.hours;
      if (trialRef.current) trialRef.current.innerText = targets.trial;
      return;
    }

    const durationMs = 4000;
    let rafId = null;
    let startTime = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (statsHasAnimatedRef.current) return;
        statsHasAnimatedRef.current = true;

        const tick = (timestamp) => {
          if (startTime === null) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / durationMs, 1);
          const eased = 1 - Math.pow(1 - progress, 3.5);

          if (studentsRef.current) studentsRef.current.innerText = Math.round(targets.students * eased);
          if (passRateRef.current) passRateRef.current.innerText = Math.round(targets.passRate * eased);
          if (hoursRef.current) hoursRef.current.innerText = Math.round(targets.hours * eased);
          if (trialRef.current) trialRef.current.innerText = Math.round(targets.trial * eased);

          if (progress < 1) {
            rafId = requestAnimationFrame(tick);
          }
        };

        rafId = requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    observer.observe(statStripRef.current);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    if (!communitySectionRef.current || communityCountHasAnimatedRef.current) return;

    const target = 174153;

    if (IS_BOT) {
      if (communityCountRef.current) communityCountRef.current.innerText = target.toLocaleString("en-US");
      return;
    }

    const durationMs = 8000;
    let rafId = null;
    let startTime = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || communityCountHasAnimatedRef.current) return;
        communityCountHasAnimatedRef.current = true;

        const tick = (timestamp) => {
          if (startTime === null) startTime = timestamp;
          const elapsed = timestamp - startTime;
          const progress = Math.min(elapsed / durationMs, 1);
          // Exponential ease-out: fast start, then visibly slower toward the end.
          const eased = progress >= 1
            ? 1
            : (1 - Math.exp(-6 * progress)) / (1 - Math.exp(-6));
          if (communityCountRef.current) communityCountRef.current.innerText = Math.round(target * eased).toLocaleString("en-US");

          if (progress < 1) {
            rafId = requestAnimationFrame(tick);
          }
        };

        rafId = requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.45 }
    );

    observer.observe(communitySectionRef.current);

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);



  return (
    <>
      <style>{css}</style>
      {!IS_BOT && (
        <div
          className={`site-loader${isPreloading ? "" : " done"}`}
          aria-hidden={!isPreloading}
          role="status"
          aria-live="polite"
        >
          <div className="loader-progress-top" aria-hidden="true">
            <span ref={progressTopRef} style={{ "--loader-progress": `0%` }} />
          </div>
          <div className="loader-split" aria-hidden="true">
            <span />
            <span />
          </div>
          <div className="loader-intro" aria-hidden="true">
            <div className="loader-phrase boring">
              <span className="loader-type loader-type-row">Boring<span className="loader-strike" /></span>
              <span className="loader-type loader-type-row">Engineering...??&nbsp;<span className="loader-strike" /></span>
            </div>
            <div className="loader-phrase anymore"><span className="loader-type">Not Anymore</span></div>
          </div>
          <div className="loader-card minimalist-wave">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
              <img src="/engimitra-logo-512.png" alt="ENGImitra Logo" style={{ width: 'clamp(110px, 18vw, 220px)', height: 'auto', objectFit: 'contain' }} />
              <div className="loader-logo-wave-base">
                ENGImitra
                <div
                  ref={progressWaveRef}
                  className="loader-logo-wave-fill"
                  style={{ "--loader-progress": 0 }}
                  aria-hidden="true"
                >
                  ENGImitra
                </div>
                <div ref={progressTextRef} className="loader-wave-percent">
                  loading... 0 %
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`site-shell${isPreloading ? " loading" : ""}`} aria-busy={isPreloading}>
        <div ref={glowRef} className="cursor-glow" />

        <Navbar />

        <main id="main-content">
          <section className="em-hero" id="hero">
            <div className="hero-bg-image"></div>

            <div className="hero-content-wrapper">
              <div className="hero-left">
                <div className="hero-trust-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  Trusted by Engineering Students
                </div>
                <h1 className="hero-main-title em-desk-only">
                  From Confusion<br />
                  <span className="text-grad">to Confidence.</span>
                </h1>
                <h1 className="hero-main-title em-mob-only">
                  <span className="mob-br">From</span>
                  Confusion <span className="text-grad">to</span><br />
                  <span className="text-pink">Confidence.</span>
                </h1>
                <p className="hero-description em-desk-only">
                  Struggling with what to study, how to<br /> pass, or clearing concepts? EngiMitra<br /> helps you understand faster, score higher,<br /> and eliminate exam fear.
                </p>
                <p className="hero-description em-mob-only">
                  Struggling with what to study, how to pass, or clearing concepts? EngiMitra helps you understand faster, score higher, and eliminate exam fear.
                </p>
              </div>

              <div className="hero-right">
                <div className="hero-features-list">
                  <div className="hf-item">
                    <div className="hf-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 2.5 2.5 0 0 1-.34-4.8 2.5 2.5 0 0 1 2.3-3.62A2.5 2.5 0 0 1 9.5 2z"></path><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 2.5 2.5 0 0 0 .34-4.8 2.5 2.5 0 0 0-2.3-3.62A2.5 2.5 0 0 0 14.5 2z"></path></svg></div>
                    <div className="hf-text">
                      <h4>CLEAR CONCEPTS</h4>
                      <p>100% Doubt Solved</p>
                    </div>
                  </div>
                  <div className="hf-item">
                    <div className="hf-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg></div>
                    <div className="hf-text">
                      <h4>FOCUSED LEARNING</h4>
                      <p>Better Understanding</p>
                    </div>
                  </div>
                  <div className="hf-item">
                    <div className="hf-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"></line><line x1="18" y1="20" x2="18" y2="4"></line><line x1="6" y1="20" x2="6" y2="16"></line></svg></div>
                    <div className="hf-text">
                      <h4>HIGH MARKS</h4>
                      <p>No Fear of Exams</p>
                    </div>
                  </div>
                  <div className="hf-item">
                    <div className="hf-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8"></path><path d="M12 17v4"></path><path d="M7 4h10"></path><path d="M5 4h14a2 2 0 0 1 2 2v2a6 6 0 0 1-6 6H9a6 6 0 0 1-6-6V6a2 2 0 0 1 2-2z"></path></svg></div>
                    <div className="hf-text">
                      <h4>CONFIDENCE</h4>
                      <p>Success in Every Exam</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="hero-bottom-bar" ref={statStripRef}>
              <div className="hero-stats-row">
                <div className="hs-item">
                  <div className="hs-val">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1f7aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path></svg>
                    <span><span ref={studentsRef}>0</span>K+</span>
                  </div>
                  <div className="hs-lbl">Students<br />Empowered</div>
                </div>
                <div className="hs-item">
                  <div className="hs-val">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1f7aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    <span><span ref={hoursRef}>0</span>+</span>
                  </div>
                  <div className="hs-lbl">Concept Videos<br />& Lectures</div>
                </div>
                <div className="hs-item">
                  <div className="hs-val">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1f7aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
                    <span><span ref={passRateRef}>0</span>%</span>
                  </div>
                  <div className="hs-lbl">Students Improved<br />Their Marks</div>
                </div>
              </div>

              <div className="hero-actions-row">
                <Link to="/courses" className="btn-solid-blue">
                  Start Learning Free <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </Link>
                <a href="#features" className="btn-outline-dark">
                  Explore Features
                </a>
              </div>
            </div>
          </section>

          <div className="ticker">
            <div className="tk-track">
              {[...Array(2)].map((_, ri) => (
                ["PROGRAMMING IN C", "ENGINEERING MATHS", "APPLIED PHYSICS", "ENGINEERING CHEMISTRY", "ENGINEERING DRAWING", "BASIC ELECTRICAL", "WORKSHOP"].flatMap((t, i) => [
                  <span key={`${ri}-${i}-t`}>{t}</span>,
                  <span key={`${ri}-${i}-d`} className="hi">◆</span>
                ])
              ))}
            </div>
          </div>
          <div className="ticker tk2">
            <div className="tk-track tk-track2">
              {[...Array(2)].map((_, ri) => (
                ["WBJEE", "JEE MAINS", "MAKAUT", "VTU", "SPPU", "AKTU", "RGPV", "GTU"].flatMap((t, i) => [
                  <span key={`${ri}-${i}-t`}>{t}</span>,
                  <span key={`${ri}-${i}-d`} className="hi">◆</span>
                ])
              ))}
            </div>
          </div>

          <section className="em-section feature-showcase" id="features">
            <div className="feature-showcase-bg">
              <Hyperspeed className="feature-hyperspeed" />
            </div>
            <div className="feature-wrap">
              <div className="feature-header-wrapper">
                <div className="feature-headline">
                  <div className="feature-marquee" aria-hidden="true">
                    <div className="feature-marquee-track">
                      <span>LEARN THINK ASK BUILD INSPIRE</span>
                      <span>LEARN THINK ASK BUILD INSPIRE</span>
                      <span>LEARN THINK ASK BUILD INSPIRE</span>
                    </div>
                  </div>
                  <h2 className="feature-tagline">
                    <span className="gold">Upskill</span> <span className="gold">Your</span> <span className="gold">Engineering</span> <br />
                    With <span className="blew">ENGI</span>&lt;mitra&gt;
                  </h2>
                </div>
                <p className="feature-subline">
                  Explore C, Maths, core engineering subjects, exam prep and mentorship with an experience designed for 1st year students.
                </p>
                <div className="feature-mini">
                  <span className="feature-mini-item">
                    <img className="feature-mini-icon1" src="/assets/signal.png" alt="" aria-hidden="true" />
                    <span>RECORDED CLASSES</span>
                  </span>
                  <span className="dotline" />
                  <span className="feature-mini-item">
                    <img className="feature-mini-icon2" src="/assets/verified.png" alt="" aria-hidden="true" />
                    <span>GET CERTIFIED</span>
                  </span>
                </div>
              </div>

              <div className="feature-cards-layout">
                <PixelCard variant="blue" gap={8} speed={22} className="pcard tall">
                  <picture>
                    <source srcSet="/assets/hmsc1.webp" type="image/webp" />
                    <img
                      src="/assets/hmsc1.png"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="pcard-image"
                      style={{ objectFit: 'cover', objectPosition: 'center 0%' }}
                    />
                  </picture>
                  <div className="pcard-content pcard-content-left">
                    <h3 className="pcard-title">Explore Professional<br />Courses</h3>
                    <Link to="/courses" className="pcard-btn">Explore Now</Link>
                  </div>
                </PixelCard>

                <PixelCard variant="default" gap={7} speed={28} className="pcard tall">
                  <picture>
                    <source srcSet="/assets/hmsc2.webp" type="image/webp" />
                    <img
                      src="/assets/hmsc2.png"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="pcard-image"
                      style={{ objectFit: 'cover', objectPosition: 'center 60%' }}
                    />
                  </picture>
                  <div className="pcard-content">
                    <h3 className="pcard-title">Learn With <br />Expert Made Notes</h3>
                    <Link to="/notes" className="pcard-btn">Get Started</Link>
                  </div>
                </PixelCard>

                <PixelCard variant="yellow" gap={5} speed={24} className="pcard short">
                  <picture>
                    <source srcSet="/assets/hmsc3.webp" type="image/webp" />
                    <img
                      src="/assets/hmsc3.png"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="pcard-image"
                      style={{ objectFit: 'cover', objectPosition: 'center 55%' }}
                    />
                  </picture>
                  <div className="pcard-content">
                    <h3 className="pcard-title">India's Best EdTech Platform</h3>
                    <Link to="/signup" className="pcard-btn">Apply Now</Link>
                  </div>
                </PixelCard>

                <PixelCard variant="pink" gap={6} speed={70} className="pcard short pcard-with-spline">
                  <Spline
                    className="pcard-spline-bg"
                    scene={SPLINE_SCENE_URL}
                  />
                  <div className="pcard-content pcard-content-left">
                    <h3 className="pcard-title">Build Your Skills</h3>
                    <Link to="/courses" className="pcard-btn">Enroll Program</Link>
                  </div>
                </PixelCard>
              </div>
            </div>
          </section>

          <section className="em-section community-sec" aria-label="Community spotlight" ref={communitySectionRef}>
            <div className="community-shell">
              <div className="community-bends">
                <ColorBends
                  colors={["#ff5c7a", "#8a5cff", "#00ffd1", "#ffb830"]}
                  rotation={90}
                  autoRotate={10}
                  speed={0.18}
                  scale={1.05}
                  frequency={1.05}
                  warpStrength={0.95}
                  mouseInfluence={1.1}
                  parallax={0.42}
                  noise={0.14}
                  iterations={2}
                  intensity={1.45}
                  bandWidth={6}
                  transparent
                />
              </div>
              <div className="community-overlay" aria-hidden="true" />
              <div className="community-copy">
                <div className="community-copy-inner">
                  <span className="community-pill">Community Spotlight</span>
                  <p className="community-lead">Join a community of students dedicated to upskilling</p>
                  <h2 className="community-count"><span ref={communityCountRef}>0</span></h2>
                  <p className="community-sub">Approximately 10,000 students enroll in ENGImitra programs every day, building momentum with focused notes, guided courses, and exam-ready prep.</p>
                  <div className="community-stats" aria-hidden="true">
                    <div className="community-stats-track">
                      <div className="community-stat">
                        <strong>Daily momentum</strong>
                        <span>New learners joining every day</span>
                      </div>
                      <div className="community-stat">
                        <strong>Exam-ready support</strong>
                        <span>Notes, MPQs, and mentor guidance</span>
                      </div>
                      <div className="community-stat">
                        <strong>Built for 1st year</strong>
                        <span>Designed for core semester needs</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="em-section presence-sec" aria-label="Our Presence Spans">
            <div className="presence-bg" aria-hidden="true">
              <div className="presence-bg-track">
                {[0, 1, 2, 3, 4, 5].map((copy) => (
                  <div className="presence-bg-group" key={`presence-bg-${copy}`}>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="presence-line">
              <span className="presence-pill">OUR PRESENCE SPANS</span>
            </div>

            <div className="presence-head">
              <h2 className="presence-title">Students from top engineering colleges grow with <span className="presence-engi">ENGI</span>&lt;mitra&gt;</h2>
              <p className="presence-sub">Students enroll in universities for degrees, but they trust ENGImitra for practical skills, exam-ready prep and career-building momentum.</p>
            </div>

            <div className="presence-rows">
              <div className="presence-row">
                <div className="presence-track">
                  {[0, 1, 2, 3, 4, 5].map((copy) => (
                    <div className="presence-group" key={`presence-r1-group-${copy}`}>
                      {presenceRowOne.map((college, index) => (
                        <div className="presence-card" key={`presence-r1-${copy}-${college.name}-${index}`}>
                          <div className="presence-logo-wrap">
                            <img src={college.logo} alt={college.name} draggable="false" />
                          </div>
                          <p className="presence-name">{college.name}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="presence-row">
                <div className="presence-track rev">
                  {[0, 1, 2, 3, 4, 5].map((copy) => (
                    <div className="presence-group" key={`presence-r2-group-${copy}`}>
                      {presenceRowTwo.map((college, index) => (
                        <div className="presence-card" key={`presence-r2-${copy}-${college.name}-${index}`}>
                          <div className="presence-logo-wrap">
                            <img src={college.logo} alt={college.name} draggable="false" />
                          </div>
                          <p className="presence-name">{college.name}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>









          <section className="testi-sec with-waves" id="reviews" aria-label="Student reviews">
            <div className="section-waves review-waves" aria-hidden="true">
              <Waves
                lineColor="rgba(255, 255, 255, 0.42)"
                backgroundColor="rgba(255, 255, 255, 0.02)"
                waveSpeedX={0.02}
                waveSpeedY={0.01}
                waveAmpX={40}
                waveAmpY={20}
                friction={0.9}
                tension={0.01}
                maxCursorMove={120}
                xGap={12}
                yGap={36}
              />
            </div>
            <div className="review-shell">
              <div className="review-head">
                <div className="review-line">
                  <span className="review-pill">Reviews</span>
                </div>
                <div className="review-bg-marquee" aria-hidden="true">
                  <div className="review-bg-track">
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                    <span>LEARN THINK ASK BUILD INSPIRE</span>
                  </div>
                </div>
                <h2 className="review-title">What our students have said</h2>
                <p className="review-sub">Our commitment to transform education speaks for itself below.</p>
              </div>

              <div className="review-ratings">
                <div className="review-rate-card">
                  <span className="review-rate-icon google">
                    <img src="/assets/google.png" alt="Google" />
                  </span>
                  <div className="review-rate-copy">
                    <small>Google Reviews</small>
                    <strong>Rated 4.6/5</strong>
                  </div>
                </div>
                <div className="review-rate-card">
                  <span className="review-rate-icon linkedin">
                    <img src="/assets/linkedin.png" alt="LinkedIn" />
                  </span>
                  <div className="review-rate-copy">
                    <small>LinkedIn Reviews</small>
                    <strong>Rated 4.3/5</strong>
                  </div>
                </div>
              </div>

              <div className="review-columns">
                {reviewColumns.map((column, colIdx) => (
                  <div className="review-col" key={`review-col-${colIdx}`}>
                    <div className={`review-col-track${colIdx % 2 ? " rev" : ""}`}>
                      {[0, 1].map((copy) => (
                        <div className="review-group" key={`review-group-${colIdx}-${copy}`}>
                          {column.map((item, itemIdx) => (
                            <article className="review-card" key={`review-item-${colIdx}-${copy}-${item.name}-${itemIdx}`}>
                              <div className="review-stars">★★★★★</div>
                              <p className="review-text">{item.text}</p>
                              <div className="review-author">
                                <span className="review-avatar">
                                  {item.avatar ? <img src={item.avatar} alt={item.name} loading="lazy" decoding="async" /> : item.emoji}
                                </span>
                                <div>
                                  <div className="review-author-name">{item.name}</div>
                                  <div className="review-author-role">{item.role}</div>
                                </div>
                              </div>
                            </article>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>





          <section className="team-sec" id="team">
            <div className="team-grid">
              <div className="team-beams-bg" aria-hidden="true">
                <Beams
                  beamWidth={1.7}
                  beamHeight={18}
                  beamNumber={14}
                  lightColor="#ffffff"
                  speed={1.8}
                  noiseIntensity={1.7}
                  scale={0.2}
                  rotation={180}
                />
              </div>
              <div className="team-backdrop-copy" aria-hidden="true">
                <div className="team-backdrop-inner">
                  <div className="team-backdrop-kicker">{'// OUR TEAM //'}</div>
                  <div className="team-backdrop-title">  Meet the Visionaries</div>
                  <div className="team-backdrop-sub">The dedicated team working to redefine engineering education.</div>
                </div>
              </div>
              <div className="team-canvas-container">
                <Lanyard
                  team={[
                    { img: "/assets/team/1.png", backImg: "/assets/team/4.png" },
                    { img: "/assets/team/2.png", backImg: "/assets/team/4.png" },
                    { img: "/assets/team/3.png", backImg: "/assets/team/4.png" }
                  ]}
                />
              </div>
              <div className="team-label-row">
                {[
                  { name: "ARNAB CHATTERJEE", role: "Founder | CEO | CTO" },
                  { name: "MRIGANKA MANDAL", role: "CMO | CFO" },
                  { name: "ADITYA GHOSH", role: "Graphics Lead" }
                ].map((m, idx) => {
                  const isVisible = !lanyardState.isMobile || (idx === lanyardState.currentIndex);
                  const isFading = lanyardState.isMobile && lanyardState.pullUp;

                  return (
                    <div
                      className={`team-item${idx === 0 ? " founder-shift" : ""}${idx === 2 ? " lead-shift" : ""}`}
                      key={idx}
                      style={lanyardState.isMobile ? {
                        position: 'absolute',
                        left: '50%',
                        bottom: '0',
                        transform: 'translateX(-50%)',
                        width: '100%',
                        textAlign: 'center',
                        opacity: isVisible && !isFading ? 1 : 0,
                        pointerEvents: isVisible ? 'auto' : 'none',
                        transition: 'opacity 0.4s ease',
                      } : {}}
                    >
                      <div className="id-info" style={lanyardState.isMobile ? { transform: 'none' } : {}}>
                        <h3 className="id-name">{m.name}</h3>
                        <span className="id-role">{m.role}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <BrandAmbassadorSection />

        </main>

        <footer className="em-footer">
          <div className="fg">
            <div className="fb">
              <a href="#hero" className="em-logo"><span className="l1">ENGI</span><span className="l2">&lt;mitra&gt;</span></a>
              <p>Built for 1st year engineering students. Expert notes, courses, and MPQs for every major Indian university — since 2022.</p>
            </div>
            <div className="fc">
              <h5>Platform</h5>
              <ul>{["Courses", "Notes", "MPQs"].map(l => <li key={l}><Link to={`/${l.toLowerCase()}`}>{l}</Link></li>)}</ul>
            </div>
            <div className="fc">
              <h5>Community</h5>
              <ul>
                <li><a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer">Instagram</a></li>
                <li><a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer">Facebook</a></li>
                <li><a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer">WhatsApp Group</a></li>
              </ul>
            </div>
            <div className="fc">
              <h5>Legal</h5>
              <ul>
              <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/refund-policy">Refund Policy</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
              </ul>
            </div>
          </div>
          <div className="fb-bottom">
            <p>© 2026 ENGImitra. All rights reserved.</p>
            <div className="soc-row">
              <a href="https://www.instagram.com/engimitra.in" target="_blank" rel="noreferrer" className="soc" aria-label="Instagram">ig</a>
              <a href="https://www.facebook.com/profile.php?id=61587673103498" target="_blank" rel="noreferrer" className="soc" aria-label="Facebook">fb</a>
              <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" className="soc" aria-label="WhatsApp Group">wa</a>
            </div>
          </div>
        </footer>

        <div className="em-fab">
          <a href="https://chat.whatsapp.com/BrKpOArPS3oCLPJnPBXD3Z" target="_blank" rel="noreferrer" title="Join WhatsApp Group">💬</a>
        </div>
        <FloatingCart />
      </div>
    </>
  );
}
